/*
  LFY — guided globe & driver selector.
  New file: assets/lfy-selector.js   (loaded with defer from snippets/lfy-selector.liquid,
  which is itself gated on template.suffix == 'two-col-wide')

  This is the browser half of the matcher verified in the session scratchpad
  (matcher.mjs: 68 self-tests, 14/14 mutation score). The verdict vocabulary is
  identical and deliberately so:

      MATCH  / CAUTION  / REJECT  / UNKNOWN        — never a boolean.

  UNKNOWN is NEVER collapsed into REJECT. Founder ruling 2026-08-10:
    "Show matched globes first, then a clearly-labelled 'colour temperature not
     specified' group ... Never collapse UNKNOWN into REJECT in the presentation
     layer — that was your 9/10 mutant, and it's a UI risk too."
  Missing data is a data-quality problem, not grounds to hide sellable stock.

  PDP JS CONTRACT — the fragile part of this page (memory lfy-pdp-js-contract):
    • The modal node is MOVED to <body> on init. The buy column is never re-rendered,
      so the gallery (slick), variants.js and the add-to-cart in theme.js are untouched.
    • Each result card is a plain <form> POST to /cart/add. product-form.js is bound to
      the main PDP form; a second AJAX form would fight it.
    • No tab markup is emitted, so tabs.js is never double-bound.
    • Nothing here queries .productView-options or .product-details.
*/
(function () {
  'use strict';

  var V = { MATCH: 'MATCH', CAUTION: 'CAUTION', REJECT: 'REJECT', UNKNOWN: 'UNKNOWN' };
  var CAP = 12;
  var REAL_SOCKETS = ['E27','E14','E12','E26','B22','B15','GU10','GU5.3','MR16','MR11',
                      'G9','G4','G5','GX53','T5','T8','R7S'];
  var PACKAGE_TYPES = ['LED','COB','SMD','LED CHIP','INTEGRATED LED','BUILT IN LED','BUILT-IN LED'];
  var CONTROLLER = /controller|receiver|touch panel|zigbee|dimmer|remote|repeater|amplifier|wall control/i;
  var CONSTANT_CURRENT = /constant current|\b\d+\s*mA\b/i;
  var FACET_LABEL = { colour: 'colour temperature', dimmable: 'dimming', wattage: 'wattage' };

  /* ── ENCLOSURE, not dimensions ────────────────────────────────────────────
     There is NO fit engine here, deliberately. Founder, 2026-08-10: 0 of 202 Havit
     lamp-taking fittings publish a max globe size. The Havit spec block has Lamp
     Base, Max Wattage, Diffuser Type, IP Rating, Protection Class — and no cavity
     field. It was never published, so no enrichment creates it.

     A single Ø x length check would also be WRONG even with the data, because the
     binding constraint differs by fitting type:
        enclosed shade/oyster -> Ø and length both bind
        narrow-neck pendant   -> Ø at the NECK, not the widest point
        downlight can         -> length/depth binds; Ø is the cutout
        open wall bracket     -> length only, and a longer globe just PROTRUDES,
                                 which is an aesthetic choice, not a failure
        batten holder         -> usually nothing binds
     So one comparison yields false REJECTS (blocking a valid sale) as well as false
     accepts. We warn on ENCLOSURE instead, and never claim or deny a fit.

     Measured live: diffuser_type is present on 2 of 3,125 lamp-taking fittings, so
     UNKNOWN is effectively the only branch today. Its wording carries the design.
     Absent diffuser_type is UNKNOWN, never OPEN — productType is a prior, not an
     answer (it classifies ~60%, and "Indoor Pendants" can be glass-shaded/enclosed). */
  var ENCLOSED_DIFFUSER = /glass|acrylic|polycarbonate|opal|frosted|diffuser|plastic|shade/i;
  var OPEN_DIFFUSER = /none|open|bare|no diffuser/i;

  function enclosure(fitting) {
    var d = asList(fitting.diffuserType).map(String).join(' ');
    if (d) {
      if (OPEN_DIFFUSER.test(d)) return 'OPEN';
      if (ENCLOSED_DIFFUSER.test(d)) return 'ENCLOSED';
    }
    return 'UNKNOWN';
  }

  // The two-tier warning. OPEN gets NOTHING — a blanket caution on every product
  // trains customers to ignore it, which is worse than no caution at all.
  function fitWarning(fitting) {
    switch (enclosure(fitting)) {
      case 'ENCLOSED':
        return 'This fitting has a ' + esc(asList(fitting.diffuserType).join(', ')).toLowerCase() +
               ' diffuser — check the globe fits inside it before ordering.';
      case 'OPEN':
        return null;
      default:
        return 'We do not hold the diffuser details for this fitting, so we cannot say ' +
               'whether a globe will sit inside it. If it has a shade or cover, check the ' +
               'globe fits before ordering.';
    }
  }

  // ---------------------------------------------------------------- helpers
  function asList(v) {
    if (v === null || v === undefined || v === '' || v === '[]') return [];
    if (Array.isArray(v)) return v;
    try { var a = JSON.parse(v); return Array.isArray(a) ? a : [a]; } catch (e) { return [v]; }
  }
  function norm(s) { return String(s).trim().toUpperCase(); }

  // null when unparseable — NEVER 0, which would silently pass a < comparison.
  function parseWatt(v) {
    var n = asList(v).map(function (x) {
      var m = String(x).match(/([\d.]+)\s*W/i); return m ? parseFloat(m[1]) : null;
    }).filter(function (x) { return x !== null && !isNaN(x); });
    return n.length ? Math.max.apply(null, n) : null;
  }
  /* ── DRIVER LOAD CALCULATOR ───────────────────────────────────────────────
     Founder, 2026-08-16, verbatim:
       "we can put load things in the driver selection..suppose someone needs to
        connect 10 step lights together making 10*5 =50W total load size..so how to
        get the right driver..load is important..some drivers have minimum load
        value..and then maximum"
       "industry standard is 20% rest you can check..but keep 25% better"
       "minimum load — we can skip it if not important"

     HEADROOM = 1.25. The founder named 20% as the industry norm and chose 25% as the
     safer side; it also matches the 80%-loading convention (a driver run at 80% of its
     rating runs cooler and lasts longer), so 1/0.8 = 1.25 is the same number arrived at
     from the other direction.

     ⛔ MINIMUM LOAD IS DELIBERATELY NOT IMPLEMENTED. Some constant-voltage drivers do
     have one, but no `min_load` metafield exists on this store and the founder has
     ruled it not important. Inferring one from description text would be inventing a
     safety figure — the exact class of thing rule 5h says to omit rather than guess.

     ⛔ THIS IS A PRESENTATION-LAYER NARROWING, NOT A VERDICT. It runs over the OUTPUT of
     matchDriver() and changes nothing about MATCH / CAUTION / REJECT / UNKNOWN. A driver
     that the matcher kept and the load filter sets aside is still a matching driver; it
     is simply too small for THIS run, and the copy says so in those words. */
  var HEADROOM = 1.25;
  var LOAD_MIN = 1;
  /* A guard against a typo, not a merchandising limit. At the low end of this catalogue
     (1W step lights) 500 fittings is 625W with headroom, which is past the largest
     driver in the pool — so the "split the run" answer stays REACHABLE from the control
     rather than being a branch only a test can enter. */
  var LOAD_MAX = 500;

  // Watts for display: 1 -> "1", 4.5 -> "4.5", 62.5 -> "62.5". Never "1.00W".
  function fmtW(n) {
    if (n === null || n === undefined || isNaN(n)) return '';
    return String(Math.round(n * 100) / 100);
  }

  function realSockets(bulbType) {
    return asList(bulbType).map(norm).filter(function (b) { return REAL_SOCKETS.indexOf(b) !== -1; });
  }
  // EXCLUSIVE match. 8 products carry G9|COB or E27|LED — those are REAL sockets and
  // must keep their selector. "contains" would wrongly strip them.
  function isBuiltInLed(bulbType) {
    var bases = asList(bulbType).map(norm).filter(Boolean);
    if (!bases.length) return false;
    return bases.every(function (b) { return PACKAGE_TYPES.indexOf(b) !== -1; });
  }
  function money(cents) {
    return '$' + (Number(cents || 0) / 100).toFixed(2).replace(/\.00$/, '');
  }
  function esc(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------- matching
  function matchGlobe(fitting, globe) {
    if (isBuiltInLed(fitting.bulbType))
      return { verdict: V.REJECT, reason: 'Built-in LED — no replaceable globe' };
    var fS = fitting.sockets || [];
    var gS = realSockets(globe.bulb_type);
    if (!fS.length) return { verdict: V.UNKNOWN, reason: 'Fitting lamp base not recorded' };
    if (!gS.length) return { verdict: V.UNKNOWN, reason: 'Globe base not recorded' };
    var shared = fS.filter(function (s) { return gS.indexOf(s) !== -1; });
    if (!shared.length) return { verdict: V.REJECT, reason: 'Base mismatch' };

    // Safety ceiling. Over max_globe_watt is a fire risk and voids warranty.
    // 0 populated this morning, 1,036 now — the enrichment lane is filling it live,
    // so this path becomes reachable on more products every batch.
    var ceiling = parseWatt(fitting.maxGlobeWatt);
    var draw = parseWatt(globe.power_watt);
    if (ceiling !== null && draw !== null && draw > ceiling)
      return { verdict: V.REJECT,
               reason: 'Exceeds this fitting’s ' + ceiling + 'W maximum' };

    // Physical fit is UNANSWERABLE — no fitting in the catalogue has cavity data.
    // Always CAUTION, never a confirmed fit. An honest warning beats a wrong answer.
    return { verdict: V.CAUTION, reason: 'Correct ' + shared.join('/') + ' base', socket: shared[0] };
  }

  // custom.voltage on a DRIVER is the INPUT (mains), not the output. 48 drivers say
  // 240V while their title says "200W - 12V Or 24V". Matching on custom.voltage
  // returns 28/118 and silently hides the rest. Resolve from the text instead.
  function driverOutput(d) {
    var t = (d.title || '') + ' ' + (d.descriptionText || '');
    var has12 = /\b12\s*V(?:\s*DC)?\b/i.test(t);
    var has24 = /\b24\s*V(?:\s*DC)?\b/i.test(t);
    if (has12 && has24) return ['12V', '24V'];
    if (has12) return ['12V'];
    if (has24) return ['24V'];
    return null;                       // caller EXCLUDES; never guesses
  }
  /* ── IP / WEATHERPROOFING ─────────────────────────────────────────────────
     An outdoor step light on an INDOOR driver FAILS. The list previously interleaved
     "Weatherproof LED Driver" and "Indoor LED Driver" with no distinction at all.
     Derive from the driver's own ip_rating where present, else its title.
     UNKNOWN is shown and LABELLED — never silently dropped, never assumed indoor. */
  function ipNumber(v) {
    var n = asList(v).map(function (x) {
      var m = String(x).match(/IP\s*(\d{2})/i); return m ? parseInt(m[1], 10) : null;
    }).filter(function (x) { return x !== null; });
    return n.length ? Math.max.apply(null, n) : null;
  }
  function weatherproofing(d) {
    var ip = ipNumber(d.ip_rating);
    if (ip !== null) return ip >= 44 ? 'WEATHERPROOF' : 'INDOOR';
    var t = (d.title || '') + ' ' + (d.descriptionText || '');
    if (/weatherproof|waterproof|outdoor/i.test(t)) return 'WEATHERPROOF';
    if (/\bindoor\b/i.test(t)) return 'INDOOR';
    return 'UNKNOWN';
  }
  // Does the FITTING need a weatherproof driver? IP44+ is outdoor-rated.
  // null = unknown; do NOT assume either way.
  function fittingNeedsWeatherproof(fitting) {
    var ip = ipNumber(fitting.ipRating);
    return ip === null ? null : ip >= 44;
  }

  /* ── VARIANT-LEVEL VOLTAGE ────────────────────────────────────────────────
     DEFECT 2026-08-10 (founder, on hv3206 @12V): the driver hv9652 offered BOTH
     "HV9652" (12V) and "HV9652-24V" (24V). A customer picking the 24V variant gets a
     driver that will not run their light. The control rendered correctly; what it
     OFFERED was wrong — the same shape as the built-in-LED row with no buttons.

     Read voltage from the variant TITLE/SKU, never `custom.voltage` (that is the
     INPUT: hv9652 holds ["240V"]).

     Self-tested against all 150 real driver-variant rows: 146 resolved, 4 inherit-base,
     0 unknown, and 69 variants are 24V-only. Crucially 52 rows are WATTAGE variants
     ("30w", "100W") that carry no voltage and must INHERIT the product's output —
     stripping those would delete most of the driver catalogue.

     Returns: array of voltages | {inheritBase:true, guess} | null (UNKNOWN). */
  function variantOutputVoltage(vTitle, sku, productOutputs) {
    var s = (vTitle || '') + ' ' + (sku || '');
    var has24 = /(?:^|[^\d])24\s*v\b/i.test(s) || /-24V/i.test(s);
    var has12 = /(?:^|[^\d])12\s*v\b/i.test(s) || /-12V/i.test(s);
    if (has24 && !has12) return ['24V'];
    if (has12 && !has24) return ['12V'];
    if (has12 && has24) return ['12V', '24V'];
    var po = productOutputs || [];
    if (po.length === 1) return [po[0]];          // wattage variant inherits
    if (po.length > 1) return { inheritBase: true, guess: po[0] };
    return null;                                   // UNKNOWN — show, label, never guess
  }

  /* Filter a driver's variants to those compatible with the fitting.
     Returns { usable, unconfirmed } — unconfirmed are shown but LABELLED. */
  function compatibleVariants(driver, wantVolts, productOutputs) {
    var usable = [], unconfirmed = [];
    var vars = driver.variants || [];
    // Does any sibling carry an explicit marker? Only then is an unmarked variant
    // safely read as "the base one".
    var anyMarked = vars.some(function (v) {
      return /(?:^|[^\d])(?:12|24)\s*v\b/i.test((v.title || '') + ' ' + (v.sku || ''));
    });
    vars.forEach(function (v) {
      var res = variantOutputVoltage(v.title, v.sku, productOutputs);
      if (res && res.inheritBase) {
        if (anyMarked && wantVolts.indexOf(res.guess) !== -1) { usable.push(v); return; }
        if (anyMarked) return;                     // a marked sibling took the other voltage
        unconfirmed.push(v); return;
      }
      if (!res) { unconfirmed.push(v); return; }   // UNKNOWN -> shown, labelled
      var hit = res.some(function (x) { return wantVolts.indexOf(x) !== -1; });
      if (hit) usable.push(v);
    });
    return { usable: usable, unconfirmed: unconfirmed };
  }

  function matchDriver(fitting, d) {
    if (CONTROLLER.test(d.title || ''))
      return { verdict: V.REJECT, reason: 'Controller, not a driver' };
    if (CONSTANT_CURRENT.test((d.title || '') + ' ' + (d.descriptionText || '')))
      return { verdict: V.REJECT, reason: 'Constant-current — not interchangeable' };
    var out = driverOutput(d);
    if (!out) return { verdict: V.UNKNOWN, reason: 'Output voltage not stated' };
    var wp = weatherproofing(d);
    var needWp = fittingNeedsWeatherproof(fitting);
    // An indoor driver on an outdoor fitting is shown but CAUTIONED — showing it is
    // deliberate (warn, never silently filter), the caution is what makes it safe.
    var ipCaution = (needWp === true && wp === 'INDOOR')
      ? 'This fitting is outdoor-rated (IP' + ipNumber(fitting.ipRating) +
        ') — an indoor driver must be installed in a weatherproof enclosure.'
      : null;
    // Which of the fitting's voltages are we matching against?
    var wantVolts = asList(fitting.voltage).map(norm)
      .map(function (x) { return /24/.test(x) ? '24V' : (/12/.test(x) ? '12V' : null); })
      .filter(Boolean);
    if (!wantVolts.length) wantVolts = out.slice();

    var cv = compatibleVariants(d, wantVolts, out);
    // ZERO compatible variants -> drop the driver entirely. Never show a product no
    // variant of which fits.
    if ((d.variants || []).length && !cv.usable.length && !cv.unconfirmed.length)
      return { verdict: V.REJECT, reason: 'No variant of this driver matches ' + wantVolts.join('/') };

    return { verdict: fitting.dualVoltage ? V.CAUTION : V.MATCH,
             reason: out.join('/') + ' DC output', outputs: out,
             wp: wp, ipCaution: ipCaution,
             usableVariants: cv.usable, unconfirmedVariants: cv.unconfirmed };
  }

  // ------------------------------------------------------- facets & grouping
  function facetMatch(item, facet, wanted) {
    if (wanted === null || wanted === undefined || wanted === '') return V.MATCH;
    if (facet === 'colour') {
      var k = asList(item.color_temperature).map(norm);
      if (!k.length) return V.UNKNOWN;
      return k.indexOf(norm(wanted)) !== -1 ? V.MATCH : V.REJECT;
    }
    if (facet === 'dimmable') {
      var d = asList(item.dimmable).map(function (x) { return String(x).toLowerCase(); });
      if (!d.length) return V.UNKNOWN;
      var isDim = d.some(function (x) { return /yes|true|dimmable/.test(x); });
      return isDim === !!wanted ? V.MATCH : V.REJECT;
    }
    if (facet === 'ip') {
      var w = weatherproofing(item);
      if (w === 'UNKNOWN') return V.UNKNOWN;      // show it, labelled — never assume
      return w === wanted ? V.MATCH : V.REJECT;
    }
    if (facet === 'wattage') {
      var w = parseWatt(item.power_watt);
      if (w === null) return V.UNKNOWN;
      return (w >= wanted[0] && w <= wanted[1]) ? V.MATCH : V.REJECT;
    }
    return V.UNKNOWN;
  }

  // Splits into confirmed / unstated. REJECT is dropped; UNKNOWN is KEPT and labelled.
  function splitByCertainty(items, choices) {
    var confirmed = [], unstated = [], missing = {};
    items.forEach(function (it) {
      var anyUnknown = false, anyReject = false;
      Object.keys(choices).forEach(function (f) {
        var v = facetMatch(it, f, choices[f]);
        if (v === V.REJECT) anyReject = true;
        if (v === V.UNKNOWN) { anyUnknown = true; missing[f] = (missing[f] || 0) + 1; }
      });
      if (anyReject) return;                 // genuinely does not match
      (anyUnknown ? unstated : confirmed).push(it);
    });
    return { confirmed: confirmed, unstated: unstated, missingFacets: missing };
  }

  function unstatedLabel(missing) {
    var names = Object.keys(missing).map(function (f) { return FACET_LABEL[f] || f; });
    if (!names.length) return null;
    var list = names.length === 1 ? names[0]
      : names.slice(0, -1).join(', ') + ' or ' + names[names.length - 1];
    return list.charAt(0).toUpperCase() + list.slice(1) + ' not specified — check before ordering';
  }

  // Zero results => relax the LAST facet, visibly. Never an empty popup, never silent.
  function relaxUntilResults(items, choices) {
    var active = {}, relaxed = [];
    Object.keys(choices).forEach(function (k) { active[k] = choices[k]; });
    var out = splitByCertainty(items, active);
    var order = ['wattage', 'dimmable', 'colour', 'ip'];
    while (out.confirmed.length + out.unstated.length === 0) {
      var next = null;
      for (var i = 0; i < order.length; i++) if (order[i] in active) { next = order[i]; break; }
      if (!next) break;
      delete active[next];
      relaxed.push(next);
      out = splitByCertainty(items, active);
    }
    out.relaxed = relaxed;
    out.relaxNote = relaxed.length
      ? 'No match on ' + relaxed.map(function (f) { return FACET_LABEL[f] || f; }).join(' or ') +
        ' — showing all ' + relaxed.map(function (f) { return FACET_LABEL[f] || f; }).join(' and ') +
        ' for your other choices.'
      : null;
    return out;
  }

  function rank(items, vendor, needWp) {
    return items.slice().sort(function (a, b) {
      // Weatherproof FIRST when the fitting is outdoor-rated (IP44+). Only applies
      // when we actually know the fitting's rating — never on a guess.
      if (needWp === true) {
        var wa = weatherproofing(a) === 'WEATHERPROOF' ? 0 : 1;
        var wb = weatherproofing(b) === 'WEATHERPROOF' ? 0 : 1;
        if (wa !== wb) return wa - wb;
      }
      var ba = (a.vendor || '').toLowerCase() === (vendor || '').toLowerCase() ? 0 : 1;
      var bb = (b.vendor || '').toLowerCase() === (vendor || '').toLowerCase() ? 0 : 1;
      if (ba !== bb) return ba - bb;                       // same brand
      var sa = a.available ? 0 : 1, sb = b.available ? 0 : 1;
      if (sa !== sb) return sa - sb;                       // then in stock
      return (a.price || 0) - (b.price || 0);              // then price
    });
  }

  // ---------------------------------------------------------------- rendering
  function card(item, fitting, kind, opts) {
    opts = opts || {};
    var same = (item.vendor || '').toLowerCase() === (fitting.vendor || '').toLowerCase();
    var bits = [];
    bits.push('<div class="lfy-sel__card' + (opts.unstated ? ' is-unstated' : '') + '">');
    bits.push('<a class="lfy-sel__card-img" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true">');
    if (item.image) bits.push('<img src="' + esc(item.image) + '" alt="" width="64" height="64" loading="lazy">');
    bits.push('</a><div class="lfy-sel__card-body">');
    bits.push('<a class="lfy-sel__card-title" href="' + esc(item.url) + '">' + esc(item.title) + '</a>');

    var tags = [];
    /* Wattage LEADS the tag row in the popup, same reasoning as the pill on the compact
       card: it is the number that decides whether a driver can carry the circuit, and
       the load calculator above filters on it. Omitted entirely when we hold no rated
       output — the "Wattage not specified" group already labels those, so a missing pill
       is a labelled absence rather than a silent one. */
    if (kind === 'driver') {
      var dwTag = parseWatt(item.power_watt);
      if (dwTag !== null) tags.push('<span class="lfy-sel__tag lfy-sel__tag--watt">' + dwTag + 'W supply</span>');
    }
    if (same) tags.push('<span class="lfy-sel__tag lfy-sel__tag--same">Same brand</span>');
    else tags.push('<span class="lfy-sel__tag lfy-sel__tag--cross">' + esc(item.vendor || 'Other brand') + '</span>');
    if (kind === 'driver' && opts.outputs) tags.push('<span class="lfy-sel__tag lfy-sel__tag--spec">' + esc(opts.outputs.join('/')) + ' DC</span>');
    // IP badge, same treatment as the voltage chip.
    if (kind === 'driver') {
      var wp = opts.wp || weatherproofing(item);
      if (wp === 'WEATHERPROOF') tags.push('<span class="lfy-sel__tag lfy-sel__tag--wp">Weatherproof</span>');
      else if (wp === 'INDOOR') tags.push('<span class="lfy-sel__tag lfy-sel__tag--indoor">Indoor only</span>');
      else tags.push('<span class="lfy-sel__tag lfy-sel__tag--ipunknown">Weatherproofing not stated</span>');
    }
    if (kind === 'globe' && opts.socket) tags.push('<span class="lfy-sel__tag lfy-sel__tag--spec">' + esc(opts.socket) + ' base</span>');
    if (!item.available) tags.push('<span class="lfy-sel__tag lfy-sel__tag--oos">Out of stock</span>');
    bits.push('<div class="lfy-sel__tags">' + tags.join('') + '</div>');

    // Cross-brand drivers are FLAGGED, never hidden (founder ruling).
    if (kind === 'driver' && !same)
      bits.push('<p class="lfy-sel__flag">Different brand to your fitting — confirm voltage, wattage and driver type before ordering.</p>');
    if (opts.ipCaution)
      bits.push('<p class="lfy-sel__flag">' + esc(opts.ipCaution) + '</p>');
    if (opts.unstatedNote)
      bits.push('<p class="lfy-sel__flag">' + esc(opts.unstatedNote) + '</p>');

    var cmp = Number(item.compareAtPrice || 0);
    var onSale = cmp > Number(item.price || 0);
    bits.push('<div class="lfy-sel__card-foot"><div class="lfy-sel__pricing">');
    bits.push('<span class="lfy-sel__price' + (onSale ? ' is-sale' : '') + '">' + money(item.price) + '</span>');
    if (onSale) {
      bits.push('<s class="lfy-sel__was">' + money(cmp) + '</s>');
      bits.push('<span class="lfy-sel__save">Save ' + money(cmp - Number(item.price)) + '</span>');
    }
    bits.push('</div>');
    if (item.available && item.variantId) {
      // Only compatible variants are selectable. Unconfirmed ones appear labelled.
      var vars = (opts.usableVariants || opts.unconfirmedVariants)
        ? (opts.usableVariants || []).concat(opts.unconfirmedVariants || [])
        : (item.variants || []);
      var unconfirmedIds = {};
      (opts.unconfirmedVariants || []).forEach(function (v) { unconfirmedIds[v.id] = true; });
      // One compatible variant left -> no pointless dropdown, add it directly.
      var multi = vars.length > 1;
      var soleId = (vars.length === 1) ? vars[0].id : item.variantId;
      // Plain POST — a second AJAX form would fight product-form.js on the PDP.
      // `.button` is the THEME's own class, so colour/hover/radius come from
      // --btn-1-* exactly like the PDP's ADD TO CART. Not a copy of its CSS.
      bits.push('<form method="post" action="/cart/add" class="lfy-sel__add">' +
                '<input type="hidden" name="form_type" value="product">' +
                '<input type="hidden" name="utf8" value="✓">' +
                '<input type="hidden" name="quantity" value="1">');
      if (multi) {
        // A "12V Or 24V" driver has a REAL choice; the wrong one does not match the
        // fitting. Add stays DISABLED until chosen — an unspecified variant reaching
        // the cart is the silent-wrong-value defect.
        /* `select__select` is the THEME's own form-control class (assets/base.css:2085)
           — the same one on the PDP's Color / variant dropdown in the buy column. Border,
           radius, caret, padding, min-height and body font all come from there, so this
           control cannot drift from the rest of the page. Our class carries SIZE only.
           Founder 2026-08-17: the old gold outline appeared nowhere else on the page. */
        bits.push('<span class="lfy-sel__variant-wrap">' +
                  '<span class="lfy-sel__variant-label">Choose voltage / type</span>' +
                  '<select name="id" class="lfy-sel__variant select__select is-unset" data-lfy-variant required>' +
                  '<option value="">Choose an option…</option>' +
                  vars.map(function (v) {
                    var note = unconfirmedIds[v.id] ? ' — voltage not specified, confirm before ordering' : '';
                    return '<option value="' + esc(v.id) + '"' + (v.available ? '' : ' disabled') + '>' +
                           esc(v.title) + note + (v.available ? '' : ' — sold out') + '</option>';
                  }).join('') + '</select></span>');
        bits.push('<button type="submit" class="lfy-sel__addbtn button button--primary" disabled>Add to cart</button>');
      } else {
        bits.push('<input type="hidden" name="id" value="' + esc(soleId) + '">');
        bits.push('<button type="submit" class="lfy-sel__addbtn button button--primary">Add to cart</button>');
      }
      bits.push('</form>');
    } else {
      bits.push('<a class="lfy-sel__addbtn lfy-sel__addbtn--view" href="' + esc(item.url) + '">View product</a>');
    }
    bits.push('</div></div></div>');
    return bits.join('');
  }

  // ---------------------------------------------------------------- init
  function init(root) {
    var dataEl = root.querySelector('[data-lfy-data]');
    var modal = root.querySelector('[data-lfy-modal]');
    if (!dataEl || !modal) return;

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var fitting = data.fitting || {};
    fitting.bulbType = (fitting.sockets || []).join(',');

    // Pre-filter to genuinely matchable stock. Collection membership is NOT the same
    // as productType (led-globes holds 492 where productType="LED Globes" is 332), so
    // re-filter here rather than trusting the collection.
    var globes = (data.globes || []).filter(function (g) {
      return matchGlobe(fitting, g).verdict === V.CAUTION;
    });
    var drivers = (data.drivers || []).filter(function (d) {
      var r = matchDriver(fitting, d);
      return r.verdict === V.MATCH || r.verdict === V.CAUTION;
    });

    /* ── TOPPING THE GLOBE POOL UP PAST SHOPIFY'S 50 ─────────────────────────────
       Nine socket collections fit inside the 50 a Liquid loop can reach; E27 has 116, so
       the page emits its first 50 and the rest are fetched here — one request per remaining
       page, against the collection's own URL through the Section Rendering API:

           /collections/e27-led-globes?section_id=lfy-globe-pool&page=2

       Deferred, never blocking: the card has already rendered from the first 50 by the time
       this runs, so a slow or failed fetch costs the customer nothing and simply leaves the
       pool at 50 — which is still 50 RELEVANT E27 globes, because the socket mapping landed
       first. That is the point of doing both.

       ⛔ The new items go through the SAME matchGlobe filter the first 50 did. Concatenating
       raw would put globes into the pool that the matcher had never judged, and they would
       be counted in "Showing 2 of N" and ranked against ones that had been. */
    function topUpGlobePool() {
      var handle = root.getAttribute('data-globe-pool');
      var total = parseInt(root.getAttribute('data-globe-pool-count') || '0', 10);
      var have = (data.globes || []).length;
      // led-globes is the unmapped fallback; topping THAT up would be 375 products of
      // mostly-irrelevant stock, which is the opposite of what the socket mapping achieved.
      if (!handle || handle === 'led-globes') return;
      if (!total || total <= have || have === 0) return;

      var pages = Math.ceil(total / 50);
      if (pages < 2) return;

      var reqs = [];
      for (var pg = 2; pg <= pages; pg++) {
        reqs.push(
          fetch('/collections/' + handle + '?section_id=lfy-globe-pool&page=' + pg,
                { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.text() : ''; })
            .then(function (html) {
              if (!html) return [];
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var node = doc.querySelector('[data-lfy-globe-page]');
              if (!node) return [];
              try { return JSON.parse(node.textContent) || []; } catch (e) { return []; }
            })
            .catch(function () { return []; })
        );
      }

      Promise.all(reqs).then(function (pagesOut) {
        var seen = {};
        globes.forEach(function (g) { seen[g.handle] = true; });
        var added = 0;
        pagesOut.forEach(function (list) {
          list.forEach(function (g) {
            if (!g || !g.handle || seen[g.handle]) return;   // page 1 overlaps by design
            seen[g.handle] = true;
            if (matchGlobe(fitting, g).verdict !== V.CAUTION) return;
            globes.push(g);
            added++;
          });
        });
        if (!added) return;
        root.setAttribute('data-globe-pool-topped', String(globes.length));
        renderInline();
        if (modal && !modal.hasAttribute('hidden')) { renderFilters(); render(); }
      }).catch(function () { /* the card keeps the pool it already had */ });
    }

    /* ── TOPPING THE DRIVER POOL UP PAST SHOPIFY'S 50 ────────────────────────────
       The globe fix left this half undone: `led-drivers` holds 87 ACTIVE and the page emits
       50, so 37 drivers reached no fitting on any product. Measured from the rendered
       payload, which reported `drivers: 50` against a `limit: 200` request.

       There is no socket-style split to lean on here. A globe is chosen by its base, which
       maps onto ten existing collections; a driver is chosen by output voltage, wattage
       headroom, dimming and IP, none of which is a collection. So the fetch is the whole fix
       rather than a top-up on top of a better pool — which makes it MORE consequential than
       the globe case, not less, and worth saying plainly: below, the first 50 are an
       arbitrary slice of the collection, not a relevant one.

       Same mechanism, same deferral, same de-dupe as topUpGlobePool. At 87 products it is
       exactly ONE extra request.

       ⛔ New items go through the SAME matchDriver filter the first 50 did, with the same
       accepted verdicts (MATCH or CAUTION). Concatenating raw would put drivers into the
       pool that the matcher never judged, and they would be counted in "Showing 2 of N",
       ranked against ones that had been, and — because the load calculator reads
       `power_watt` off whatever is in the pool — could be recommended for a load nobody
       checked them against.

       ⛔ matchDriver is CALLED here, never modified. */
    function topUpDriverPool() {
      var handle = root.getAttribute('data-driver-pool');
      var total = parseInt(root.getAttribute('data-driver-pool-count') || '0', 10);
      var have = (data.drivers || []).length;
      // `have === 0` means this fitting needs no driver at all, so there is nothing to top
      // up and no request to make. Distinct from "the fetch failed".
      if (!handle) return;
      if (!total || total <= have || have === 0) return;

      var pages = Math.ceil(total / 50);
      if (pages < 2) return;

      var reqs = [];
      for (var pg = 2; pg <= pages; pg++) {
        reqs.push(
          fetch('/collections/' + handle + '?section_id=lfy-driver-pool&page=' + pg,
                { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.text() : ''; })
            .then(function (html) {
              if (!html) return [];
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var node = doc.querySelector('[data-lfy-driver-page]');
              if (!node) return [];
              try { return JSON.parse(node.textContent) || []; } catch (e) { return []; }
            })
            .catch(function () { return []; })
        );
      }

      Promise.all(reqs).then(function (pagesOut) {
        var seen = {};
        drivers.forEach(function (d) { seen[d.handle] = true; });
        var added = 0;
        pagesOut.forEach(function (list) {
          list.forEach(function (d) {
            if (!d || !d.handle || seen[d.handle]) return;   // page 1 overlaps by design
            seen[d.handle] = true;
            var r = matchDriver(fitting, d);
            if (r.verdict !== V.MATCH && r.verdict !== V.CAUTION) return;
            drivers.push(d);
            added++;
          });
        });
        if (!added) return;
        root.setAttribute('data-driver-pool-topped', String(drivers.length));
        renderInline();
        if (modal && !modal.hasAttribute('hidden')) { renderFilters(); render(); }
      }).catch(function () { /* the card keeps the pool it already had */ });
    }

    /* ── TIERS 3 & 4 — THE BEST-SELLERS FALLBACK (founder decision D1-o) ────────
       "Bestsellers if nothing comes up in accessories or anything … no globes, no
        drivers, no vendor + Product type bestsellers, then in that case we have
        best sellers collections use that"

       The rail shows EXACTLY ONE tier, the first that yields results:
         1  matching globes            }  the card shell — already built
         2  matching drivers           }
         3  best sellers in vendor + productType
         4  the `best-selling-products` collection

       ⛔ TIERS 3 AND 4 ARE NOT COMPATIBILITY-MATCHED, and the copy must never imply
       otherwise. Tiers 1-2 carry a MATCH/CAUTION verdict from the
       matcher; these two carry none, because none was computed. The v4 mockup's
       "Same product type · no accessory needed" is deliberately NOT used: this tier
       fires when we found no matching accessory, which on a 12V fitting means "we
       stock no driver that matches", NOT "no driver is needed". Printing the
       mockup's line there would tell a customer they can wire a 12V fitting
       straight to mains. Same class of defect as PENDING P59.

       WHERE THE DATA COMES FROM, and why it is a fetch.

       Tier 4 is a collection, so Liquid could read it. Tier 3 is not: "best sellers
       in vendor + productType" exists only as a FILTERED collection URL, and Liquid
       cannot apply a storefront filter. Liquid can iterate
       `collections['best-selling-products'].products` 50 at a time; the collection
       holds 882 products (measured: "Showing 0 of 882 products" on a filtered-empty
       render), so a Liquid tier 3 would either miss nearly every vendor+type pair
       or need ~18 offset queries added to the TTFB of every gated product.

       So both tiers come from ONE storefront endpoint, differing only by the filter
       query — which keeps them a single code path and therefore a single card
       style:

         /collections/best-selling-products
             ?section_id=main-collection-product-grid
             [&filter.p.vendor=…&filter.p.product_type=…]

       ⚠ THE FILTER IS PROVEN APPLIED, not assumed. This store is at the Search &
       Discovery 25-filter cap and a non-enabled filter is silently ignored — a
       filtered URL then returns an unfiltered list and looks like it worked. Two
       controls, both run before this code was written:
         · positive — vendor=Havit Lighting + product_type=Step Lights returns 20
           products and every one of them IS Havit Lighting / Step Lights.
         · negative — vendor=ZZZNope returns the collection's EMPTY STATE and
           "Showing 0 of 882 products". A filter that were being ignored could not
           produce zero.
       ⚠ `/collections/all?filter.p.…` does NOT filter on this store. Do not
       "simplify" the endpoint to it.

       ⛔ ARCHIVED PRODUCTS. The known trap is that the Admin API's BEST_SELLING sort
       includes archived products. Nothing here touches the Admin API: this reads a
       rendered STOREFRONT collection, and the storefront cannot serve an archived or
       draft product at all — it 404s. That is the structural reason; it is also
       asserted in verification by resolving every rendered handle.

       COST. One request, ~44KB over the wire, issued only when tiers 1-2 produced
       nothing AND the viewport is >=1500 (the rail is the only place this renders —
       D1-o trap 2). A second request only if tier 3 came back short. The majority of
       gated products have a matching accessory and pay nothing. */
    /* VIABILITY threshold, not the display count. D1-o: "render nothing at all rather than
       a list of one", so a tier needs at least 2 items to be a tier. Deliberately NOT raised
       to 4 with the display count below — that would reject a perfectly good 3-item
       vendor+type list and fall through to store-wide best sellers, which is LESS relevant,
       to satisfy a purely visual target. */
    var FB_ROWS = 2;
    /* How many actually render. The mock's best-sellers box draws FOUR rows, and as its own
       box beside the finder that is the right density; when it REPLACES the finder it keeps
       the original two, which is what made it read like the tiers 1-2 list it stands in for. */
    var FB_SHOW_COMPANION = 4;
    var fbState = { started: false };
    var fbEligible = false;       // set by whichever branch found tiers 1-2 empty

    function fbQuery(fb) {
      if (!fb.vendor || !fb.productType) return null;
      return 'filter.p.vendor=' + encodeURIComponent(fb.vendor) +
             '&filter.p.product_type=' + encodeURIComponent(fb.productType);
    }

    /* Returns the theme's OWN rendered cards. `product-grid-layout` is what
       snippets/halo-side-product-block.liquid renders and what the collection grid
       renders, so nothing here invents a card — founder ruling, see the
       `ella-check-first` skill. The cards arrive already rendered by the theme; we
       only choose which ones and where they go. */
    function fbFetch(fb, filterQS) {
      var base = fb.collectionUrl || ('/collections/' + fb.collectionHandle);
      var url = base + '?section_id=main-collection-product-grid' +
                (filterQS ? '&' + filterQS : '');
      return fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var lis = doc.querySelectorAll('#main-collection-product-grid > li.product');
        var out = [];
        Array.prototype.forEach.call(lis, function (li) {
          var a = li.querySelector('a[href*="/products/"]');
          if (!a) return;
          var m = String(a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
          if (!m) return;
          var h = m[1];
          // Never recommend the page the customer is already on. The fitting is
          // frequently its OWN category's top seller — hv3206-wht… is #1 for
          // Havit Lighting + Step Lights, which is exactly a product that would
          // reach tier 3.
          if (h === fb.selfHandle) return;
          for (var i = 0; i < out.length; i++) if (out[i].handle === h) return;
          out.push({ handle: h, node: li });
        });
        return out;
      });
    }

    function fbRender(tier, items, fb, companion) {
      var mount = root.querySelector('[data-lfy-bestsellers]');
      if (!mount) return false;
      var type = fb.productType || '';
      var title, sub, allUrl, allLabel;
      if (tier === 3) {
        /* THE MOCK'S OWN WORDING. Founder 2026-08-18 on the old sub — "Havit Lighting ·
           popular, not compatibility-matched" — "whats that??". It named the vendor for a
           list the customer can already see is one vendor, and "not compatibility-matched"
           was internal vocabulary explaining what the box ISN'T.

           v4 said it plainly: as a companion the box is bsTitle 'Best sellers' with the
           product type as its sub; standing in for an absent accessory it becomes
           'Best sellers in <Type>' / 'Same product type · no accessory needed'.
           ⚠ THAT SECOND SUBTITLE WAS REVERTED on 2026-08-20 — see the note on the `sub`
           line below. The companion half of v4 stands unchanged.
           D1-q.5's honesty requirement still holds — nothing here claims a match, it claims
           popularity, which is what it is. */
        title = companion ? 'Best sellers' : 'Best sellers in ' + type;
        /* ⛔ THE SUBTITLE IS DELIBERATELY EMPTY IN THE NON-COMPANION BRANCH. Founder
           2026-08-20, reading the live driver page: "Same product type · no accessory
           needed --< do we need this ??"

           No, and it was worse than redundant. Two reasons, both measured:

           1. It made a compatibility claim this branch cannot support. Non-companion
              means the finder card produced no rows, which collapses TWO different
              situations that the code cannot tell apart here: the product genuinely
              needs no accessory, and we stock nothing that matches one it does need.
              The comment at the head of this section already said exactly that, and
              said this string was "deliberately NOT used" for that reason — then the
              v4 wording overrode it and the warning was left standing. Code and
              comment now agree again.

           2. It fired on products the selector never examined. 16 live products
              sampled: 5 carried the line and FOUR were ceiling fans (52" Iluka DC,
              Caprice AC, Nemoi DC, Guardian IP55). This selector only knows globes and
              drivers. It has no basis for telling a customer a DC fan needs no wall
              control.

           'Best sellers in <Type>' already says everything the box is. The span is not
           created at all when empty — an empty span still carries the sub's line-height
           and left a gap under the title. */
        sub = companion ? type : '';
        allUrl = (fb.collectionUrl || '') + '?' + fbQuery(fb);
        allLabel = 'View all ' + (type || 'best sellers');
      } else {
        // Tier 4 is not even the same product type, and D1-o is explicit that it must not
        // silently reuse tier 3's wording. The mock does not draw this case.
        title = 'Best sellers';
        sub = 'Popular across the store';
        allUrl = fb.collectionUrl || '';
        allLabel = 'View all best sellers';
      }

      mount.textContent = '';
      mount.setAttribute('data-lfy-tier', String(tier));

      var hd = document.createElement('div');
      hd.className = 'lfy-sel__bs-hd';
      /* The mock's own mark: a star on grey (#5f6a78), the neutral counterpart to the
         finder's navy tile, so the two boxes read as siblings rather than as one card that
         changed subject halfway down. */
      var ico = document.createElement('span');
      ico.className = 'lfy-sel__bs-ico';
      ico.setAttribute('aria-hidden', 'true');
      ico.textContent = '★';
      hd.appendChild(ico);
      var t = document.createElement('span');
      t.className = 'lfy-sel__bs-title';
      t.textContent = title;
      var txt = document.createElement('span');
      txt.className = 'lfy-sel__bs-txt';
      txt.appendChild(t);
      if (sub) {
        var s = document.createElement('span');
        s.className = 'lfy-sel__bs-sub';
        s.textContent = sub;
        txt.appendChild(s);
      }
      hd.appendChild(txt);
      mount.appendChild(hd);

      var ul = document.createElement('ul');
      ul.className = 'lfy-sel__bs-list';
      /* ── THE MOCK'S COMPACT ROW, NOT THE THEME'S GRID CARD ───────────────────────
         Founder 2026-08-18: "can we match to exact to mock design for bestsellers, instead
         of showing theme product card..big??"

         ⚠ THIS REVERSES AN EARLIER RULING, deliberately. D1-o chose the theme's own rendered
         card via ella-check-first, so that nothing invented a card. That was right for a box
         standing in for the product grid; it is wrong in a 280px rail, where the grid card is
         several times taller than the row the mock draws and pushes "View all" off screen.
         The founder has now seen both and picked the mock.

         The card is still the SOURCE — every field is read off the theme's own markup, so
         titles, prices and images stay whatever the theme says they are. Nothing is
         re-fetched and no price is recomputed here. */
      items.slice(0, companion ? FB_SHOW_COMPANION : FB_ROWS).forEach(function (it, i) {
        var card = it.node;
        var a = card.querySelector('a[href*="/products/"]');
        var href = a ? String(a.getAttribute('href') || '').split('?')[0] : '';
        var nameEl = card.querySelector('.card-title');
        var name = nameEl ? nameEl.textContent.trim().replace(/\s+/g, ' ') : '';
        /* The FIRST price-item is the current price. The whole .price node is not used
           because it also carries the compare-at and the "Unit price / per" microcopy —
           "$76.72 $103.95$76.72 Unit price / per" measured on a real card. */
        var priceEl = card.querySelector('.price-item--sale, .price-item--regular, .price-item');
        var price = priceEl ? priceEl.textContent.trim().replace(/\s+/g, ' ') : '';
        var srcImg = card.querySelector('img');
        if (!href || !name) return;

        var li = document.createElement('li');
        li.className = 'lfy-sel__bs-row';
        li.setAttribute('data-lfy-bs-handle', it.handle);

        var th = document.createElement('a');
        th.className = 'lfy-sel__bs-th';
        th.href = href;
        th.setAttribute('tabindex', '-1');
        th.setAttribute('aria-hidden', 'true');
        if (srcImg) {
          var img = document.createElement('img');
          img.src = srcImg.getAttribute('src') || '';
          if (srcImg.getAttribute('srcset')) img.srcset = srcImg.getAttribute('srcset');
          /* The card arrives with the COLLECTION GRID's sizes hint, which picks a file made
             for a 260px tile and paints it into a 46px thumb — wasteful, and on a 2x display
             the wrong file entirely. Correct the hint, never the URL. */
          img.setAttribute('sizes', '46px');
          img.setAttribute('alt', '');
          img.setAttribute('loading', 'lazy');
          img.setAttribute('decoding', 'async');
          th.appendChild(img);
        }
        li.appendChild(th);

        var mid = document.createElement('div');
        mid.className = 'lfy-sel__bs-mid';
        var nm = document.createElement('a');
        nm.className = 'lfy-sel__bs-nm';
        nm.href = href;
        nm.textContent = name;
        mid.appendChild(nm);
        var meta = document.createElement('div');
        meta.className = 'lfy-sel__bs-meta';
        /* Every row carries its product type and nothing else.
           The v4 mock marked only the FIRST row "· best seller" and this followed it, but the
           founder read the rendered result and it does not survive contact: the box is already
           HEADED "Best sellers", so tagging row 1 alone implies rows 2-4 are not — the opposite of
           what the box means. Founder, 2026-08-20, seeing "Outdoor Wall Lights · best seller"
           above three untagged siblings: "why best seller word is in the titles?"
           Deliberate departure from the mock, like the compact rail rows and the navy-not-amber
           driver mode before it. */
        meta.textContent = type || '';
        if (meta.textContent) mid.appendChild(meta);
        li.appendChild(mid);

        if (price) {
          var pr = document.createElement('div');
          pr.className = 'lfy-sel__bs-pr';
          pr.textContent = price;
          li.appendChild(pr);
        }
        ul.appendChild(li);
      });
      if (!ul.children.length) return false;
      mount.appendChild(ul);

      if (allUrl) {
        var a = document.createElement('a');
        a.className = 'lfy-sel__bs-all';
        a.href = allUrl;
        a.textContent = allLabel;
        var arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = ' →';
        a.appendChild(arrow);
        mount.appendChild(a);
      }
      mount.removeAttribute('hidden');
      return true;
    }

    function runFallback() {
      if (fbState.started) return;
      // D1-o trap 2: the rail is >=1500 only. Nothing here renders on phone/tablet.
      if (window.innerWidth < 1500) return;
      var fb = (data && data.fallback) || {};
      // A missing collection means no tier 4 and no base URL for tier 3 — do
      // nothing rather than fetch a 404. (The first build of this selector pointed
      // at two collections that did not exist and rendered empty, silently.)
      if (!fb.collectionHandle) return;
      fbState.started = true;

      var qs = fbQuery(fb);
      var tier3 = qs ? fbFetch(fb, qs).catch(function () { return []; })
                     : Promise.resolve([]);

      tier3.then(function (t3) {
        // D1-o trap 2: "show fewer rather than padding with unrelated stock, and
        // render nothing at all rather than a list of one". A single-item tier is
        // not a tier — fall through to the next one.
        if (t3.length >= FB_ROWS) return { tier: 3, items: t3 };
        return fbFetch(fb, null)
          .then(function (t4) { return (t4.length >= FB_ROWS) ? { tier: 4, items: t4 } : null; })
          .catch(function () { return null; });
      }).then(function (res) {
        if (!res) return;
        /* COMPANION vs REPLACEMENT — founder 2026-08-18, following the mock.

           v4 draws best sellers as a SECOND box in the aside, permanently, below the
           finder (`.abox.plain`), and only at 1920-with-no-accessory does it become the
           whole rail. It was built here as a replacement — it hid the finder shell
           whenever it rendered — so it could never be the companion the mock shows.

           Companion when the finder has something to say: rows on screen, or rows the LOAD
           filter removed (which is still an answer, with advice attached). Replacement only
           when the finder genuinely has nothing, which is the original D1-o behaviour and
           is left exactly as it was. */
        /* ⛔ `hdState` and `loadSuppressedRows` are declared LATER in init (lines ~1890 and
           ~1046) than the early return for an empty pool (~1003). On a product where both
           accessory pools are empty, init returns before those declarations execute, so by the
           time this callback runs they are hoisted-but-`undefined` — and `hdState.shown` threw
           a TypeError inside this `.then()`, which the `.catch()` below swallowed by design
           ("leave the page exactly as it was"). The visible result was that Best Sellers never
           appeared on such a product, with no error anywhere: runFallback resolved tier 4 with
           20 items and then died one line before rendering them.

           That never mattered while the selector was gated to ~450 products which all had
           accessory data. Assigning the whole catalogue to two-col-wide on 2026-08-19 made it
           the common case — roughly half of 7,681 products.

           Guarded rather than moved: `companion` is false on that path by definition (there are
           no rows to companion), which is exactly what the D1-o replacement behaviour wants.
           Moving the declarations up would work too but reorders init for every product to fix
           one branch. */
        var companion = !!(hdState && hdState.shown > 0) || !!loadSuppressedRows;
        var shell = root.querySelector('[data-lfy-shell]');
        if (shell && !companion) shell.setAttribute('hidden', '');
        if (!fbRender(res.tier, res.items, fb, companion)) return;
        root.removeAttribute('hidden');
        root.classList.toggle('lfy-sel--fallback', !companion);
        root.classList.toggle('lfy-sel--has-bs', companion);
        placeSelector();          // hoisted; creates the rail and MOVES the node in
      }).catch(function () { /* leave the page exactly as it was */ });
    }

    /* TEST HOOK. It changes nothing by itself — it empties the two accessory pools
       and re-runs the SAME runFallback() the page uses, so a test exercises the
       production decision and the production render rather than a mock.

       It exists because the tier-3/4 branch is reachable in principle but rare in
       this catalogue: 492 globes and 95 drivers mean most gated fittings match
       something, and a rendering that has never been executed is not a rendering
       that has been verified. Passing a `type` that matches nothing is how the
       tier-3 -> tier-4 FALL-THROUGH gets exercised for real, rather than by
       short-circuiting to tier 4. */
    root.__lfyTest = {
      forceFallback: function (opts) {
        globes.length = 0;
        drivers.length = 0;
        if (opts && typeof opts.type === 'string' && data.fallback) data.fallback.productType = opts.type;
        fbState.started = false;
        fbEligible = true;
        /* Deliberately does NOT hide the accessory shell. Hiding it here was the
           first version and it made the sub-1500 check unfalsifiable: the hook
           hid the card, runFallback() then correctly declined to render anything
           below 1500, and the page was left with NOTHING — which a test reads as
           "the fallback is off at this width" while it is actually a hook
           artefact. Hiding the shell is runFallback()'s job, on success only. */
        runFallback();
      }
    };

    // If nothing matches at all, do not show a trigger that opens an empty popup.
    // Tiers 1 and 2 are empty, so the chain moves on to tier 3.
    if (!globes.length && !drivers.length) {
      root.setAttribute('hidden', '');
      fbEligible = true;
      runFallback();
      window.addEventListener('resize', function () {
        setTimeout(function () { if (fbEligible) runFallback(); }, 200);
      });
      return;
    }

    // Move the modal out of the buy column so no ancestor is ever re-rendered.
    document.body.appendChild(modal);

    var mode = globes.length ? 'globe' : 'driver';
    var choices = {};
    var showAll = false;

    var elFilters = modal.querySelector('[data-lfy-filters]');
    var elResults = modal.querySelector('[data-lfy-results]');
    var elUnstated = modal.querySelector('[data-lfy-unstated]');
    var elRelax = modal.querySelector('[data-lfy-relax]');
    var elShowAll = modal.querySelector('[data-lfy-showall]');
    var elFitWarn = modal.querySelector('[data-lfy-fitwarn]');

    function pool() { return mode === 'globe' ? globes : drivers; }

    /* ═══════════════════════════════════════════════════════════════════════
       DRIVER LOAD CALCULATOR

       ⛔ NOT A VERDICT. Everything below runs over `drivers`, which is already the
       matcher's OUTPUT (matchDriver returned MATCH or CAUTION). No verdict is read,
       written or reinterpreted here; MATCH / CAUTION / REJECT / UNKNOWN are untouched
       and UNKNOWN is still never collapsed into REJECT.

       ⛔ MISSING WATTAGE NEVER SILENTLY FILTERS — standing founder ruling. A driver
       with no `power_watt` goes into the labelled "not specified" group the component
       already uses for dimming and colour temperature; it is never dropped. And when
       the FITTING has no wattage we cannot compute anything, so we say that in words
       and leave the list exactly as it was — an unfiltered honest list beats a filtered
       wrong one.
       ═══════════════════════════════════════════════════════════════════════ */
    /* null when unparseable, NEVER 0 — a 0 unit load would compute a 0W requirement and
       silently pass every driver, which is "undefined reached a guard as a pass". */
    var unitWatt = parseWatt(fitting.powerWatt);
    var loadState = { qty: 1 };
    var loadUid = 0;
    var loadMounts = [];
    /* Set by renderInline() when the LOAD filter (not an empty pool) is why no rows
       rendered. Without it a high quantity would read as "tiers 1-2 produced nothing"
       and hand the rail to the best-sellers fallback — hiding the very message that
       explains what happened. Tiers 1-2 DID produce results; they are all too small. */
    var loadSuppressedRows = false;

    function loadTotals() {
      if (unitWatt === null) return null;
      var q = loadState.qty;
      var total = unitWatt * q;
      /* Round before ceiling. 2.4 * 5 * 1.25 is 15.000000000000004 in IEEE 754, and a
         bare Math.ceil turns that into 16W — a requirement one watt too high, produced
         by nothing but binary fractions. */
      var required = Math.ceil(Math.round(total * HEADROOM * 10000) / 10000);
      return { qty: q, unit: unitWatt, total: total, required: required };
    }

    /* Split the matcher's driver list three ways against the computed requirement.
       `maxW` spans EVERY rated driver in the pool, including the ones that are too
       small, because it is what the "split the run" advice is arithmetic on. */
    function loadPartition(items) {
      var t = loadTotals();
      if (!t) return null;
      var meets = [], unknown = [], under = [], maxW = null;
      items.forEach(function (it) {
        var w = parseWatt(it.power_watt);
        if (w === null) { unknown.push(it); return; }   // shown + labelled, never dropped
        if (maxW === null || w > maxW) maxW = w;
        (w >= t.required ? meets : under).push(it);
      });
      return { t: t, meets: meets, unknown: unknown, under: under, maxW: maxW };
    }

    /* Move the no-wattage drivers out of `confirmed` and into the labelled group, and
       record the count so unstatedLabel() names wattage alongside dimming/colour. Shared
       by the popup and the inline card so the two cannot disagree about what is shown. */
    function labelUnknownWatt(out, lp) {
      if (!lp || !lp.unknown.length) return out;
      var unk = {};
      lp.unknown.forEach(function (u) { unk[u.handle] = true; });
      var keep = [];
      out.confirmed.forEach(function (it) {
        if (unk[it.handle]) out.unstated.push(it); else keep.push(it);
      });
      out.confirmed = keep;
      var n = out.unstated.filter(function (it) { return unk[it.handle]; }).length;
      if (n) out.missingFacets.wattage = n;
      return out;
    }

    function readoutText() {
      var t = loadTotals();
      if (!t) return '';
      // Show the ARITHMETIC, not just the answer — the customer should see where the
      // number came from, and be able to check it.
      return t.qty + ' × ' + fmtW(t.unit) + 'W = ' + fmtW(t.total) +
             'W total · needs a driver rated ' + fmtW(t.required) + 'W or higher (25% headroom)';
    }

    // The fitting has no wattage: no input at all. An input that cannot change anything
    // is a dead control, and a dead control is worse than an absent one (rules 6c/6d).
    function loadNoWattHtml(compact) {
      return compact
        ? 'We do not hold a wattage for this fitting, so we cannot size a driver to your ' +
          'run. Read it off the data sheet, multiply by the number of fittings, add 25% — ' +
          'and have your electrician confirm. Every matching driver is shown below.'
        : 'We do not hold a wattage for this fitting, so we cannot work out the total load ' +
          'of your circuit. Read the wattage off the data sheet, multiply it by the number ' +
          'of fittings on the circuit and add 25% headroom — then have your electrician ' +
          'confirm it. Nothing is filtered out below; every matching driver is listed.';
    }

    /* The real-world answer when the run is bigger than anything we stock. An empty list
       is not an answer, and neither is silence. Both surfaces get the same reasoning; the
       rail gets it in fewer words because it is 280px wide, and keeping both strings in
       ONE function is what stops them drifting apart. */
    function loadNoMatchHtml(lp, compact) {
      var t = lp.t, R = fmtW(t.required);
      var n = (lp.maxW !== null && lp.maxW > 0) ? Math.ceil(t.required / lp.maxW) : null;
      if (compact) {
        return '<div class="lfy-sel__load-none"><strong>Nothing we stock is rated ' + R +
          'W or higher.</strong> ' +
          (n !== null
            ? 'Our largest is ' + fmtW(lp.maxW) + 'W, so this run needs at least ' + n +
              ' drivers — split the fittings into ' + n + ' groups, one driver each.'
            : 'Split the run across two or more drivers, one group of fittings each.') +
          ' Your electrician can confirm the split.</div>';
      }
      return '<div class="lfy-sel__empty">' +
        '<strong>No single driver we stock is rated ' + R + 'W or higher</strong>' +
        t.qty + ' × ' + fmtW(t.unit) + 'W is ' + fmtW(t.total) + 'W, which needs ' + R +
        'W once 25% headroom is added. ' +
        (n !== null
          ? 'The largest matching driver we hold is ' + fmtW(lp.maxW) + 'W, so this run needs at least ' +
            n + ' drivers — split the fittings into ' + n + ' groups and give each group its own driver. '
          : 'Split the run across two or more drivers, each carrying its own group of fittings. ') +
        'Lower the count above to size a single driver, or ask your electrician to confirm the split.' +
        '</div>';
    }

    function loadBlockHtml(compact) {
      if (unitWatt === null) {
        return '<div class="lfy-sel__load-in">' +
               '<p class="lfy-sel__load-out is-nowatt" data-lfy-load-out>' +
               esc(loadNoWattHtml(compact)) + '</p></div>';
      }
      var uid = 'lfy-load-q' + (++loadUid);
      return '<div class="lfy-sel__load-in">' +
        '<label class="lfy-sel__load-label" for="' + uid + '">How many fittings on this circuit?</label>' +
        // The advice the old footnote used to give, moved to the moment it is acted on.
        '<span class="lfy-sel__load-hint">Count every fitting that will run from the one ' +
        'driver, not just this one.</span>' +
        '<span class="lfy-sel__load-row">' +
          '<input id="' + uid + '" class="lfy-sel__load-qty" type="number" inputmode="numeric" ' +
          'min="' + LOAD_MIN + '" max="' + LOAD_MAX + '" step="1" value="' + loadState.qty +
          '" data-lfy-qty aria-label="How many fittings on this circuit">' +
          '<span class="lfy-sel__load-unit">fittings</span>' +
        '</span>' +
        '<p class="lfy-sel__load-out" data-lfy-load-out aria-live="polite"></p>' +
      '</div>';
    }

    // Built ONCE. Afterwards only the readout's textContent changes, so typing in the
    // number input never rebuilds the element the caret is sitting in.
    function mountLoad(el, compact) {
      if (!el) return;
      el.innerHTML = loadBlockHtml(compact);
      loadMounts.push(el);
    }

    function syncLoadUI(exceptInput) {
      var txt = readoutText();
      loadMounts.forEach(function (el) {
        var out = el.querySelector('[data-lfy-load-out]');
        if (out && unitWatt !== null) out.textContent = txt;
        var inp = el.querySelector('[data-lfy-qty]');
        if (inp && inp !== exceptInput && String(loadState.qty) !== inp.value) inp.value = loadState.qty;
      });
    }

    /* LIVE, not submit-gated — the list narrows as the number changes, exactly as the
       facet chips do. `commit` is the blur/change pass: only then is the field itself
       normalised, so clearing it to retype does not fight the customer by snapping
       back to 1 on the first keystroke. */
    function applyQty(inp, commit) {
      var raw = String(inp.value == null ? '' : inp.value).trim();
      if (raw === '') { if (commit) inp.value = loadState.qty; return; }
      var n = parseInt(raw.replace(/[^\d]/g, ''), 10);
      if (isNaN(n)) { if (commit) inp.value = loadState.qty; return; }
      if (n < LOAD_MIN) n = LOAD_MIN;
      if (n > LOAD_MAX) n = LOAD_MAX;
      if (commit) inp.value = n;
      var changed = (n !== loadState.qty);
      loadState.qty = n;
      syncLoadUI(commit ? null : inp);
      if (!changed) return;
      renderInline();
      if (modal && !modal.hasAttribute('hidden')) render();
    }

    function wireQty(scope) {
      if (!scope) return;
      scope.addEventListener('input', function (e) {
        var inp = e.target && e.target.closest ? e.target.closest('[data-lfy-qty]') : null;
        if (inp) applyQty(inp, false);
      });
      scope.addEventListener('change', function (e) {
        var inp = e.target && e.target.closest ? e.target.closest('[data-lfy-qty]') : null;
        if (inp) applyQty(inp, true);
      });
    }

    if (fitting.needsDriver && drivers.length) {
      mountLoad(root.querySelector('[data-lfy-load-inline]'), true);
      mountLoad(modal.querySelector('[data-lfy-load]'), false);
      syncLoadUI();
      wireQty(root);
      wireQty(modal);
    }

    function facetOptions() {
      var src = pool(), cts = {}, watts = [];
      src.forEach(function (it) {
        asList(it.color_temperature).forEach(function (k) { cts[norm(k)] = 1; });
        var w = parseWatt(it.power_watt); if (w !== null) watts.push(w);
      });
      return {
        colours: Object.keys(cts).sort(),
        wattRange: watts.length ? [Math.min.apply(null, watts), Math.max.apply(null, watts)] : null
      };
    }

    function renderFilters() {
      var o = facetOptions(), h = [];
      if (mode === 'globe' && o.colours.length) {
        h.push('<div class="lfy-sel__facet"><span class="lfy-sel__facet-label">Colour temperature</span><div class="lfy-sel__chips">');
        h.push('<button type="button" class="lfy-sel__chip' + (!choices.colour ? ' is-on' : '') + '" data-facet="colour" data-val="">Any</button>');
        o.colours.forEach(function (c) {
          h.push('<button type="button" class="lfy-sel__chip' + (choices.colour === c ? ' is-on' : '') +
                 '" data-facet="colour" data-val="' + esc(c) + '">' + esc(c) + '</button>');
        });
        h.push('</div></div>');
      }
      if (mode === 'driver') {
        h.push('<div class="lfy-sel__facet"><span class="lfy-sel__facet-label">Location</span><div class="lfy-sel__chips">');
        h.push('<button type="button" class="lfy-sel__chip' + (!choices.ip ? ' is-on' : '') + '" data-facet="ip" data-val="">Any</button>');
        h.push('<button type="button" class="lfy-sel__chip' + (choices.ip === 'WEATHERPROOF' ? ' is-on' : '') + '" data-facet="ip" data-val="WEATHERPROOF">Weatherproof</button>');
        h.push('<button type="button" class="lfy-sel__chip' + (choices.ip === 'INDOOR' ? ' is-on' : '') + '" data-facet="ip" data-val="INDOOR">Indoor</button>');
        h.push('</div></div>');
      }
      h.push('<div class="lfy-sel__facet"><span class="lfy-sel__facet-label">Dimmable</span><div class="lfy-sel__chips">');
      h.push('<button type="button" class="lfy-sel__chip' + (choices.dimmable === undefined ? ' is-on' : '') + '" data-facet="dimmable" data-val="">Any</button>');
      h.push('<button type="button" class="lfy-sel__chip' + (choices.dimmable === true ? ' is-on' : '') + '" data-facet="dimmable" data-val="yes">Dimmable</button>');
      h.push('</div></div>');
      elFilters.innerHTML = h.join('');
    }

    function render() {
      /* LOAD NARROWING — applied to the matcher's output before the facets run.
         `lp` is null in globe mode and null whenever the fitting has no wattage, and in
         both cases everything below behaves exactly as it did before this feature. */
      var lp = (mode === 'driver') ? loadPartition(pool()) : null;
      var elLoad = modal.querySelector('[data-lfy-load]');
      if (elLoad) {
        if (mode === 'driver' && drivers.length) elLoad.removeAttribute('hidden');
        else elLoad.setAttribute('hidden', '');
      }
      var basePool = lp ? lp.meets.concat(lp.unknown) : pool();

      var out;
      if (lp && !basePool.length) {
        /* Nothing survived the LOAD. Do not hand this to relaxUntilResults: it would
           strip colour, dimming and IP hunting for results the load removed, and print
           a note blaming a facet the customer never set. The load message below says
           what actually happened. */
        out = { confirmed: [], unstated: [], missingFacets: {}, relaxNote: null };
      } else {
        out = relaxUntilResults(basePool, choices);
      }
      out = labelUnknownWatt(out, lp);

      if (out.relaxNote) { elRelax.textContent = out.relaxNote; elRelax.removeAttribute('hidden'); }
      else { elRelax.setAttribute('hidden', ''); elRelax.textContent = ''; }

      var needWp = (mode === 'driver') ? fittingNeedsWeatherproof(fitting) : null;
      var conf = rank(out.confirmed, fitting.vendor, needWp);
      var uns = rank(out.unstated, fitting.vendor, needWp);
      var shown = showAll ? conf : conf.slice(0, CAP);

      var html = '';
      if (shown.length) {
        html += shown.map(function (it) {
          var r = mode === 'globe' ? matchGlobe(fitting, it) : matchDriver(fitting, it);
          return card(it, fitting, mode, { socket: r.socket, outputs: r.outputs, wp: r.wp, ipCaution: r.ipCaution, usableVariants: r.usableVariants, unconfirmedVariants: r.unconfirmedVariants });
        }).join('');
      } else if (lp && !lp.meets.length) {
        /* THE REAL-WORLD ANSWER, not an empty list. Rendered even when the "not
           specified" group below has rows, because those rows do not answer the
           question the customer just asked. */
        html += loadNoMatchHtml(lp, false);
      } else if (!uns.length) {
        html += '<div class="lfy-sel__empty"><strong>Nothing matches those choices</strong>' +
                'We could not find a ' + (mode === 'globe' ? 'globe' : 'driver') +
                ' for this fitting with those filters. Clear a filter above, or contact us ' +
                'and we will source one for you.</div>';
      }
      elResults.innerHTML = html;

      // The "not specified" group — visible, labelled, never silently dropped.
      if (uns.length) {
        var label = unstatedLabel(out.missingFacets) || 'Details not specified';
        // Wattage gets its own sentence: "may still suit" is too breezy for a driver we
        // cannot confirm is big enough for the load the customer just entered.
        var unsSub = (lp && out.missingFacets.wattage)
          ? 'We do not hold a rated output for these, so we cannot confirm they cover your ' +
            fmtW(lp.t.required) + 'W requirement — check before ordering.'
          : 'These may still suit — we just do not hold that detail yet.';
        elUnstated.innerHTML =
          '<div class="lfy-sel__unstated-head"><strong>' + esc(label) + '</strong>' +
          '<span class="lfy-sel__unstated-sub">' + esc(unsSub) + '</span></div>' +
          uns.slice(0, CAP).map(function (it) {
            var r = mode === 'globe' ? matchGlobe(fitting, it) : matchDriver(fitting, it);
            return card(it, fitting, mode, { socket: r.socket, outputs: r.outputs, wp: r.wp, ipCaution: r.ipCaution, usableVariants: r.usableVariants, unconfirmedVariants: r.unconfirmedVariants, unstated: true });
          }).join('');
        elUnstated.removeAttribute('hidden');
      } else {
        elUnstated.setAttribute('hidden', '');
        elUnstated.innerHTML = '';
      }

      if (conf.length > CAP && !showAll) {
        elShowAll.textContent = 'Show all ' + conf.length + ' matches';
        elShowAll.removeAttribute('hidden');
      } else elShowAll.setAttribute('hidden', '');

      // Two-tier enclosure warning. Rendered ONLY for globes, and only when there is
      // something honest to say — an OPEN fitting gets no caution at all.
      var elInlineWarn = root.querySelector('[data-lfy-fitwarn-inline]');
      if (elInlineWarn) {
        var iw = fitWarning(fitting);
        if (iw && (mode === 'globe' || globes.length)) { elInlineWarn.innerHTML = iw; elInlineWarn.removeAttribute('hidden'); }
        else elInlineWarn.setAttribute('hidden', '');
      }
      if (elFitWarn) {
        var warn = (mode === 'globe') ? fitWarning(fitting) : null;
        if (warn) { elFitWarn.innerHTML = warn; elFitWarn.style.display = ''; }
        else { elFitWarn.style.display = 'none'; }
      }

      // innerHTML was just rebuilt — restore the done rows it wiped.
      if (typeof reapplyDone === 'function') reapplyDone(modal);
    }

    // ------------------------------------------------------------- events
    modal.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-facet]');
      if (chip) {
        var f = chip.getAttribute('data-facet'), val = chip.getAttribute('data-val');
        if (!val) delete choices[f];
        else if (f === 'dimmable') choices.dimmable = true;
        else if (f === 'ip') choices.ip = val;
        else choices[f] = val;
        showAll = false;
        renderFilters(); render();
        return;
      }
      var tab = e.target.closest('[data-lfy-tab]');
      if (tab) {
        mode = tab.getAttribute('data-lfy-tab');
        choices = {}; showAll = false;
        Array.prototype.forEach.call(modal.querySelectorAll('[data-lfy-tab]'), function (t) {
          t.classList.toggle('is-active', t === tab);
        });
        renderFilters(); render();
        return;
      }
      if (e.target.closest('[data-lfy-showall]')) { showAll = true; render(); return; }
      if (e.target.closest('[data-lfy-close]')) { close(); }
    });

    /* ── ADD TO CART -> the theme's own slide-out drawer ──────────────────────
       A plain form POST redirects to /cart, which throws the customer off the
       fitting page they were buying. The theme's own ATC instead POSTs to
       /cart/add.js and then opens the sidebar (theme.js:6585 expressAjaxAddToCart:
       `$body.addClass('cart-sidebar-show'); halo.updateSidebarCart(cart)`).
       We call that SAME path.

       This does NOT breach the PDP JS contract. The contract forbids binding a second
       handler to the main product form (product-form.js owns it). We never touch that
       form — these are our own forms inside a modal on <body>, and we use the theme's
       public cart helper rather than re-implementing it. If the helper is missing for
       any reason we fall back to a normal submit, so the button always works. */
    /* Extracted so the MODAL and the INLINE block share one implementation. The
       inline cards live outside the modal, and duplicating this logic would let the
       two drift — the inline Add behaving differently from the popup Add is exactly
       the kind of divergence nobody notices until a customer hits it. */
    function handleAddSubmit(e, form) {
      if (!form) return;
      var idField = form.querySelector('[name="id"]');
      if (!idField || !idField.value) return;            // let the browser show the required-field UI

      // The theme's drawer is opened by its own header cart icon. `window.halo` is
      // NOT global (it is closed over inside theme.js), so calling it directly always
      // failed and silently fell back to a full-page redirect to /cart.
      var cartIcon = document.querySelector('[data-cart-sidebar]');
      if (!cartIcon) return;                             // no drawer on this theme -> normal POST

      e.preventDefault();
      // Both classes: the popup tiles use .lfy-sel__addbtn, the compact rows use
      // .lfy-sel__rcard-add. Matching only the first left `btn` null on every inline
      // row, so the "Adding…" state never showed there.
      var btn = form.querySelector('.lfy-sel__addbtn, .lfy-sel__rcard-add');
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

      /* IDEMPOTENCY — the add and the read-back are two requests, and only the first
         one changes the cart. Recording its success separately is what stops the
         catch below from re-POSTing an add that already landed. */
      var added = false;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'id=' + encodeURIComponent(idField.value) + '&quantity=1'
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        added = true;                                     // the cart HAS changed from here on
        return fetch('/cart.js').then(function (c) { return c.json(); });
      }).then(function (cart) {
        /* DONE STATE GATE — the cart must CONFIRM the variant, not merely `r.ok`.
           A 200 that added nothing still resolves ok, and "the button changed" is
           exactly the weaker assertion that let a dead Add button pass once already.
           So we read /cart.js back and look for the id we posted. No confirmation ->
           no done state, and the button is restored. */
        var wanted = String(idField.value);
        var inCart = ((cart && cart.items) || []).some(function (i) {
          return String(i.variant_id) === wanted || String(i.id) === wanted;
        });
        if (inCart) markAdded(form, wanted);
        else if (btn) { btn.textContent = 'Added'; }
        close();                                          // hand the screen to the drawer
        // Let the theme open AND populate its own drawer. Clicking its bound icon runs
        // the theme's handler, so we never re-implement the cart UI.
        try { cartIcon.click(); } catch (err) { /* best effort */ }
        // Refresh the header count the way the theme's own sections do.
        try {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        } catch (err) { /* optional */ }
        // Only restore a button that still exists — the done badge replaced it otherwise.
        if (!inCart) setTimeout(function () { if (btn) { btn.disabled = false; btn.textContent = label; } }, 1200);
      }).catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = label; }

        /* Only re-POST if the add never landed. The old code called form.submit()
           on ANY rejection, including a rejection thrown by the /cart.js read-back
           AFTER a successful add — which duplicated the line item and hard-navigated
           to /cart/add. Observed for real: Cloudflare served an HTML bot challenge to
           the second request, so .json() threw on an add that had already succeeded.
           The fallback exists so a customer is never left with a dead button; it must
           not become a way to charge them for two. */
        if (!added) { form.submit(); return; }

        // The add succeeded and only the read-back failed. Nothing to retry — hand the
        // screen to the theme's drawer, which fetches its own cart state.
        close();
        try { cartIcon.click(); } catch (err) { /* best effort */ }
      });
    }
    modal.addEventListener('submit', function (e) {
      handleAddSubmit(e, e.target.closest('.lfy-sel__add'));
    });

    // Enable Add only once a variant is chosen. Adding an unspecified variant is the
    // silent-wrong-value defect in the cart.
    function syncVariantButton(sel) {
      if (!sel) return;
      // Scope to the FORM, not sel.parentElement. When the select was wrapped in
      // .lfy-sel__variant-wrap (to give it a label on mobile), the parent stopped
      // being the form and started being the wrapper — which holds no button. So
      // `btn` was null, the enable never ran, and Add to cart stayed permanently
      // dead on every row that had a variant select. Founder found it.
      var form = sel.closest('.lfy-sel__add') || sel.closest('form');
      // Compact inline rows put the <select> OUTSIDE its form (form="" association),
      // so closest() finds nothing — look up the row instead. Rule 6c: new markup
      // means the old traversal silently stops matching.
      if (!form) {
        var rowEl = sel.closest('.lfy-sel__rcard');
        if (rowEl) form = rowEl.querySelector('.lfy-sel__add');
      }
      var btn = form ? form.querySelector('.lfy-sel__addbtn, .lfy-sel__rcard-add') : null;
      var chosen = !!sel.value;
      if (btn) btn.disabled = !chosen;
      sel.classList.toggle('is-unset', !chosen);
    }
    modal.addEventListener('change', function (e) {
      syncVariantButton(e.target.closest('[data-lfy-variant]'));
    });

    function open() {
      modal.removeAttribute('hidden');
      document.body.classList.add('lfy-sel-open');
      renderFilters(); render();
      var x = modal.querySelector('[data-lfy-close].lfy-sel__x');
      if (x) x.focus();
    }
    function close() {
      modal.setAttribute('hidden', '');
      document.body.classList.remove('lfy-sel-open');
      var trig = root.querySelector('[data-lfy-open]');
      if (trig) trig.focus();
    }
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-lfy-open]')) { e.preventDefault(); open(); }
    });

    /* The inline cards are rendered OUTSIDE the modal, so the modal-scoped submit and
       change listeners never see them. Without this the inline Add to Cart would be
       dead and the inline variant select would never enable — exactly the shape that
       already shipped once (lfy-pdp-verify rule 6c). Same handlers, root scope. */
    root.addEventListener('submit', function (e) {
      var form = e.target.closest('.lfy-sel__add');
      if (form) handleAddSubmit(e, form);
    });
    root.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-lfy-variant]');
      if (sel) syncVariantButton(sel);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) close();
    });

    // Open directly onto the mode the clicked button asks for (two-button row).
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-lfy-open][data-open-mode]');
      if (!btn) return;
      var want = btn.getAttribute('data-open-mode');
      if ((want === 'globe' && globes.length) || (want === 'driver' && drivers.length)) {
        mode = want;
        Array.prototype.forEach.call(modal.querySelectorAll('[data-lfy-tab]'), function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-lfy-tab') === want);
        });
      }
    }, true);

    /* ── PHASE 1: TWO MATCHES INLINE ──────────────────────────────────────────
       Founder-approved "Inline C". PRESENTATION ONLY — this calls the SAME
       matchGlobe/matchDriver/rank/splitByCertainty used by the popup and renders the
       same card(). No verdict is computed here; if this function disagreed with the
       popup that would be a bug, not a feature.

       Why it matters: median 65 driver options per fitting (min 62). Most customers
       should never need the popup, so the two best matches come to them. */
    /* The mockup's compact row (.rcard): one flex line — 52px thumb, name, one meta
       line, then price and a small Add on the right, separated by hairlines rather
       than borders. Deliberately NOT card(): that is the popup's tile, and shrinking
       it gave a bordered block with a wrapped footer, which is what the founder
       flagged as "not matched with mockup".

       Same verdict object, same variant filtering, same money() — presentation only. */
    function inlineRow(item, f, kind, r) {
      var same = (item.vendor || '').toLowerCase() === (f.vendor || '').toLowerCase();
      var meta = [];
      if (kind === 'driver' && r.outputs) meta.push(r.outputs.join('/') + ' DC');
      if (kind === 'globe' && r.socket) meta.push(r.socket);
      if (kind === 'driver') {
        var wp = r.wp || weatherproofing(item);
        if (wp === 'WEATHERPROOF') meta.push('Weatherproof');
        else if (wp === 'INDOOR') meta.push('Indoor only');
      }
      if (!item.available) meta.push('Out of stock');

      var vars = (r.usableVariants || r.unconfirmedVariants)
        ? (r.usableVariants || []).concat(r.unconfirmedVariants || [])
        : (item.variants || []);
      var multi = vars.length > 1;
      var soleId = (vars.length === 1) ? vars[0].id : item.variantId;

      /* The mockup gives each row a contextual REASON ("Outdoor rated · runs up to 6
         Fino", "Headroom to add more later") and a MATCH/CAUTION chip. Both are read
         from the verdict the matcher already returned — nothing is recomputed. */
      var why = [];
      var wattPill = '';
      if (kind === 'driver') {
        var wpv = r.wp || weatherproofing(item);
        if (wpv === 'WEATHERPROOF') why.push('Outdoor rated');
        else if (wpv === 'INDOOR') why.push('Indoor only');
        /* The rated output moves OUT of the grey `why` line and into its own pill —
           founder: it is the number that decides whether a driver can carry the
           circuit, and buried in "Outdoor rated · 20W supply" it does not read as one.
           Drivers are single-variant products on this store (Havit ships HV9663-30W,
           -60W and -100W as three products, not one with three variants), so a
           product-level figure is the whole truth here; there is no variant-level
           wattage metafield and none is needed. */
        var dw = parseWatt(item.power_watt);
        if (dw !== null) wattPill = dw + 'W supply';
      } else {
        var gw = parseWatt(item.power_watt);
        if (gw !== null) why.push(gw + 'W');
        var kk = asList(item.color_temperature).map(norm);
        if (kk.length) why.push(kk.join('/'));
        var dd = asList(item.dimmable).map(function (x) { return String(x).toLowerCase(); });
        if (dd.some(function (x) { return /yes|true|dimmable/.test(x); })) why.push('Dimmable');
      }
      // CAUTION when the matcher itself cautioned, or when anything is unstated.
      var chip = (r.verdict === V.MATCH) ? 'MATCH' : 'CAUTION';
      if (r.ipCaution || r.brandNote) chip = 'CAUTION';

      var b = [];
      /* The row carries its OWN kind. Since a dual-need card renders a globe section and
         a driver section together, "which kind did the customer just add?" can no longer
         be answered from a single card-level mode — markAdded() reads this attribute, so
         adding a driver from a card whose first section is globes still says "Driver
         added". Reading the mode instead would have named the wrong accessory. */
      b.push('<div class="lfy-sel__rcard" data-lfy-kind="' + esc(kind) + '">');
      b.push('<a class="lfy-sel__rcard-th" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true">');
      if (item.image) b.push('<img src="' + esc(item.image) + '" alt="" width="52" height="52" loading="lazy">');
      b.push('</a>');
      b.push('<div class="lfy-sel__rcard-main">');
      b.push('<a class="lfy-sel__rcard-nm" href="' + esc(item.url) + '">' + esc(item.title) + '</a>');
      b.push('<div class="lfy-sel__rcard-why">' + esc(why.join(' · ')) + '</div>');
      b.push('<span class="lfy-sel__chip lfy-sel__chip--' + chip.toLowerCase() + '">' + chip + '</span>');
      /* No pill when we hold no rated output. The absence is not a silent filter — the
         load calculator already groups those under "Wattage not specified — check
         before ordering", so a missing pill is consistent with a labelled group rather
         than a quiet omission. */
      if (wattPill) b.push('<span class="lfy-sel__watt">' + esc(wattPill) + '</span>');
      b.push('<div class="lfy-sel__rcard-meta">' +
             (same ? '<span class="lfy-sel__rcard-brand">Same brand</span>' :
                     '<span class="lfy-sel__rcard-brand lfy-sel__rcard-brand--cross">' + esc(item.vendor || '') + '</span>') +
             (meta.length ? '<span class="lfy-sel__rcard-facts">' + esc(meta.join(' · ')) + '</span>' : '') +
             '</div>');
      b.push('</div>');

      b.push('<div class="lfy-sel__rcard-buy">');
      var cmp = Number(item.compareAtPrice || 0);
      var onSale = cmp > Number(item.price || 0);
      b.push('<span class="lfy-sel__rcard-pr">' + money(item.price) + '</span>');
      if (onSale) b.push('<s class="lfy-sel__rcard-was">' + money(cmp) + '</s>');
      if (item.available && item.variantId) {
        /* The <select> lives INSIDE the form. It used to sit in the row's main column
           and reach the form via `form="lfy-inline-…"` association — which broke the
           submit interception: the inline variant Add posted normally and REDIRECTED
           TO /cart, throwing the customer off the fitting page. The single-variant row
           was unaffected, so it looked fine until the variant case was exercised.

           Two lessons worth keeping: `form=` association put the control outside the
           element our handler scopes to (rule 6c again, via an attribute rather than a
           wrapper), and `form.id` is NOT reliable for reading it back — a form's named
           controls shadow its properties, so with `<input name="id">` present,
           `form.id` returns that INPUT, not the id string. */
        b.push('<form method="post" action="/cart/add" class="lfy-sel__add">' +
               '<input type="hidden" name="form_type" value="product">' +
               '<input type="hidden" name="utf8" value="✓">' +
               '<input type="hidden" name="quantity" value="1">');
        if (multi) {
          // Same theme class as the popup tile — see the note in card().
          b.push('<select name="id" class="lfy-sel__variant select__select is-unset" data-lfy-variant required>' +
                 '<option value="">Choose an option…</option>' +
                 vars.map(function (v) {
                   return '<option value="' + esc(v.id) + '"' + (v.available ? '' : ' disabled') + '>' +
                          esc(v.title) + (v.available ? '' : ' — sold out') + '</option>';
                 }).join('') + '</select>');
        } else {
          b.push('<input type="hidden" name="id" value="' + esc(soleId) + '">');
        }
        b.push('<button type="submit" class="lfy-sel__rcard-add"' + (multi ? ' disabled' : '') + '>Add</button></form>');
      } else {
        b.push('<a class="lfy-sel__rcard-add lfy-sel__rcard-add--view" href="' + esc(item.url) + '">View</a>');
      }
      b.push('</div></div>');
      return b.join('');
    }

    var elInline = root.querySelector('[data-lfy-inline]');

    function plural(noun, n) { return noun + (n === 1 ? '' : 's'); }

    /* How many rows the POPUP would show for a kind — computed with the SAME functions
       the popup and the inline list already use (loadPartition / splitByCertainty /
       labelUnknownWatt). No second definition of "matching" is introduced: if this
       disagreed with the popup that would be rule 5j, one rule enforced in two places,
       and the number on the rail is the one the customer checks the popup against.
       No matcher function is called and no verdict is read. */
    function kindTotal(kind) {
      var p = (kind === 'globe') ? globes : drivers;
      if (!p.length) return 0;
      var lp = (kind === 'driver') ? loadPartition(p) : null;
      var pi = lp ? lp.meets.concat(lp.unknown) : p;
      var o = labelUnknownWatt(splitByCertainty(pi, {}), lp);
      return o.confirmed.length + o.unstated.length;
    }

    /* Extracted so the normal path and the load-no-match path emit the SAME button.
       Two hand-written copies of a control that opens the popup is how one of them ends
       up without `data-open-mode` and quietly opens the wrong tab.

       `parts` is [{n, noun}, …] with the CURRENT kind first. Zero-count kinds are
       dropped, so the label can never advertise a tab with nothing behind it, and
       `data-open-mode` is taken from the FIRST SURVIVING part rather than from the
       inline kind — otherwise a driver card whose rows the load filter removed would
       open onto an empty Drivers tab while the globes it is offering sit one tab away.

       ⛔ DEFECT 2026-08-17 (founder, on hv3608-ss316 Taper): this button is the ONLY
       [data-lfy-open] in the component — nothing is server-rendered. It used to be
       gated on `total > top.length`, so a fitting whose matching list is 2 or fewer
       got NO opener, and with it no popup and no Drivers tab. On a fitting needing
       BOTH a globe and a driver that hid 13 matching drivers behind a gate keyed to
       the GLOBE count. Founder ruling D1-q.4: always render the opener, gated on
       EITHER list having matches. Hence the caller no longer conditions on the count,
       and the emptiness test lives here, in one place. */
    function seeAllHtml(parts) {
      var live = (parts || []).filter(function (x) { return x && x.n > 0; });
      if (!live.length) return '';
      var label = (live.length === 1)
        ? 'See all ' + live[0].n + ' matching ' + plural(live[0].noun, live[0].n)
        : 'See all ' + live.map(function (x) { return x.n + ' ' + plural(x.noun, x.n); }).join(' and ');
      return '<button type="button" class="lfy-sel__seeall" data-lfy-open ' +
             'data-open-mode="' + esc(live[0].noun) + '">' + esc(label) +
             ' <span aria-hidden="true">→</span></button>';
    }

    /* ── ONE SECTION PER KIND ─────────────────────────────────────────────────────
       Founder, 2026-08-17, on hv3608-ss316: "why i can see only globe option selector
       that is open while bottom its showing both drivers and globes??"

       A fitting needing BOTH used to render ONE inline list. `mode` starts at 'globe'
       whenever any globe matches, so the card showed globes and nothing else, beneath an
       opener that named both kinds — "See all 2 globes and 29 drivers". The drivers were
       real and reachable, but only by opening the popup and switching tab, and nothing on
       the card said so. The summary promised two things and the body delivered one.

       Each needed kind now gets its own section, still capped at 2 rows. The pool, the
       load filter, the certainty split and the ranking are untouched and still come from
       the matcher — this stops DISCARDING one of the two answers it already produced.
       No matcher function is modified; matchGlobe/matchDriver/splitByCertainty/rank are
       called exactly as before. */
    function inlineSection(kind) {
      var pool = (kind === 'globe') ? globes : drivers;
      if (!pool.length) return null;

      /* The SAME load narrowing the popup applies, over the same pool. The calculator's
         input lives in the popup and in the rail card, but the STATE is one object and
         the filter is one function — two surfaces that disagreed about which drivers
         suit a 50W run would be the "one rule enforced in two places" defect (rule 5j),
         and here it would be the customer's shopping list that diverged. */
      var lp = (kind === 'driver') ? loadPartition(pool) : null;
      var narrowed = lp ? lp.meets.concat(lp.unknown) : pool;

      // Same split and same ranking as the popup — confirmed first, unstated after.
      var out = labelUnknownWatt(splitByCertainty(narrowed, {}), lp);
      var needWp = (kind === 'driver') ? fittingNeedsWeatherproof(fitting) : null;
      var ranked = rank(out.confirmed, fitting.vendor, needWp);
      var top = ranked.slice(0, 2);
      var total = ranked.length + out.unstated.length;

      if (!top.length) {
        /* Rows removed by the LOAD, not by an empty pool. Say what happened and what to
           do about it; hiding the section would leave a card whose calculator had just
           silently emptied it. An genuinely empty pool returns null and renders nothing. */
        return (lp && !lp.meets.length)
          ? { kind: kind, html: loadNoMatchHtml(lp, true), rows: 0, total: total, loadSuppressed: true }
          : null;
      }

      var constraint = [];
      if (kind === 'driver') {
        var wv = asList(fitting.voltage).map(norm)
          .map(function (x) { return /24/.test(x) ? '24V' : (/12/.test(x) ? '12V' : null); })
          .filter(Boolean);
        if (wv.length) constraint.push(wv.join('/'));
        if (fittingNeedsWeatherproof(fitting) === true) constraint.push('outdoor rated');
      } else {
        if (fitting.sockets && fitting.sockets.length) constraint.push(fitting.sockets.join('/'));
        var cl = parseWatt(fitting.maxGlobeWatt);
        if (cl !== null) constraint.push('max ' + cl + 'W');
      }

      /* ── DEFECT 2026-08-17 (founder, on hv2901-ab Pinta) ──────────────────────
         The rail read "2 globes shown" beside an opener reading "See all 4 matching
         globes", and the two were read as CONTRADICTING each other. Measured on the
         preview theme: the popup renders exactly 4 globes, 0 unstated, so both numbers
         were already correct — the rail is a deliberate short list of the top 2 and
         "N shown" simply never said so. This is a WORDING fix, not a logic one; the
         pool, the ranking and the cap are untouched.

         "Showing 2 of 4 globes" names the ratio, so the opener's 4 is the same 4.
         When the short list IS the whole list (2 of 2, the Taper) the ratio would be
         noise, so it keeps the plain form. */
      var headN = (total > top.length)
        ? 'Showing ' + top.length + ' of ' + total + ' ' + plural(kind, total)
        : top.length + ' ' + plural(kind, top.length) + ' shown';

      var html = '<div class="lfy-sel__rhead"><span class="lfy-sel__rhead-n">' +
                 esc(headN) + '</span>' +
                 (constraint.length ? '<span class="lfy-sel__rhead-c">' + esc(constraint.join(' · ')) + '</span>' : '') +
                 '</div>' +
                 '<div class="lfy-sel__inline-list">' +
        top.map(function (it) {
          var r = (kind === 'globe') ? matchGlobe(fitting, it) : matchDriver(fitting, it);
          return inlineRow(it, fitting, kind, r);
        }).join('') + '</div>';

      return { kind: kind, html: html, rows: top.length, total: total, loadSuppressed: false };
    }

    function renderInline() {
      if (!elInline) return;
      hdState.shown = 0;                 // every early return below means "no pill"

      /* ONE LIST AT A TIME, and the segmented control is how the other is reached.
         v4's `both` panel: `selSeg: true` beside `resTitle: '2 globes match'` — one heading,
         one list. An earlier build here stacked both lists; that predated anyone opening the
         mock and has been replaced rather than kept as a variant. */
      var dual = !!(globes.length && drivers.length);
      var active = (globes.length && !drivers.length) ? 'globe'
                 : (drivers.length && !globes.length) ? 'driver'
                 : mode;
      var kinds = dual ? ['globe', 'driver'] : (globes.length ? ['globe'] : drivers.length ? ['driver'] : []);
      var sections = [inlineSection(active)].filter(Boolean);

      /* The inline load calculator belongs to the DRIVER list, so it follows the active tab
         rather than the fitting's needs — on the Globe tab there is nothing to size. */
      var elLoadI = root.querySelector('[data-lfy-load-inline]');
      var hasDriver = sections.some(function (s) { return s.kind === 'driver'; });
      if (elLoadI) {
        if (hasDriver) elLoadI.removeAttribute('hidden');
        else elLoadI.setAttribute('hidden', '');
      }

      loadSuppressedRows = sections.some(function (s) { return s.loadSuppressed; });

      if (!sections.length) { elInline.setAttribute('hidden', ''); return; }

      /* The mock's opener names ONLY the visible kind — "See all 41 matching globes →".
         That was previously unsafe: with no toggle, an opener that failed to mention drivers
         made the Drivers tab unreachable (D1-q.4). The segmented control is exactly what
         removes that risk, so the mock's wording becomes correct again. Still UNCONDITIONAL:
         D1-q.4's other half was that the opener must render whether or not this kind has
         rows, which is what made a popup reachable at all. */
      var openerParts = [{ n: kindTotal(active), noun: active }];

      elInline.innerHTML = sections.map(function (s) { return s.html; }).join('') +
                           seeAllHtml(openerParts);
      elInline.removeAttribute('hidden');

      /* Feed the v4 header states. The pill counts JOBS when the fitting needs both —
         v4's state table: "'2 jobs' when the fitting needs BOTH" — and ROWS when it needs
         one kind ("2 match"). Counting rows in both modes would make a dual-need card
         read "4 jobs" now that two sections render, when there are still only two jobs:
         find a globe, find a driver. */
      var rows = sections.reduce(function (n, s) { return n + s.rows; }, 0);
      /* ⛔ JOBS, NOT SECTIONS. Counting rendered sections was right while both lists stacked;
         with the segmented control exactly ONE section ever renders, so it collapsed to
         "1 job" on a fitting that plainly has two. v4 is explicit — the `both` panel's
         closedPill is "2 jobs" — and a job exists because the FITTING needs that kind, not
         because a tab happens to be showing it. Reached only when a section rendered, so the
         best-sellers fallback below still sees shown === 0 on a card with nothing to offer. */
      hdState.shown = dual ? kinds.length : rows;
      hdState.total = sections.reduce(function (n, s) { return n + s.total; }, 0);
      /* With two kinds on screen the card no longer HAS a single inline kind. markAdded()
         reads the kind off the row itself, so this stays meaningful only for the
         single-kind case and falls back to the tab's mode otherwise. */
      hdState.inlineKind = (sections.length === 1) ? sections[0].kind : '';
      hdState.both = dual;
      reapplyDone(elInline);
      syncSeg();
      syncHeader();
    }

    /* ── V4 HEADER STATES: the collapsed "N match" pill, and the done state ──────
       Source of truth: `PDP Responsive v4 - resequenced.dc.html`, panels 3 and 4:

         768 "selector closed" — "collapsed to one line with a '2 match' pill · tap to open"
         768 "driver added"    — "done state · green tick, '✓ In cart' pill, tap to change"

       and its state table (SEL / KIND):
         closed: selPill '2 match'  ('2 jobs' when the fitting needs BOTH)
         done:   selIco  = tick, selTitle 'Driver added — …',
                 selSub  'Tap to change or add another', selPill '✓ In cart'
         open:   selPill '' — the pill exists ONLY while collapsed.

       PRESENTATION ONLY. No verdict, no ranking and no matcher function is touched;
       the pill counts the rows renderInline() already produced.

       AMBER: v4's .c-caution (#fdf5e6/#8a6a2e) is deliberately NOT adopted — founder
       ruling, twice. The green here is v4's own success green and is ALREADY in
       lfy-selector.css as .lfy-sel__chip--match (#e8f6ee / #1a8a4a), so no new colour
       enters the palette. Navy stays var(--btn-1-bg). */
    var hdState = { shown: 0, total: 0, both: false, inlineKind: '',
                    done: false, doneName: '', kind: '' };
    var addedIds = {};                       // variant id -> confirmed in the cart
    /* Which of the two JOBS are done — not which variants. A dual-need fitting asks the
       customer for two different things, and the segmented control needs to show a ✓ on the
       one already satisfied (Inline C panel 3b). Keyed by kind, so adding a second globe
       does not un-tick anything and adding a driver ticks the other tab independently. */
    var doneKinds = {};

    /* v4's tick: viewBox 0 0 24 24, stroke-width 2.2, round caps, path M4 12.5l5.2 5L20 7.
       NO width/height ATTRIBUTES — attributes beat CSS and the header icon then cannot
       be resized (the founder hit exactly that with the globe/driver icons). Size comes
       from the stylesheet. */
    function tickSvg(cls) {
      return '<svg class="' + cls + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
             'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" ' +
             'aria-hidden="true" focusable="false"><path d="M4 12.5l5.2 5L20 7"/></svg>';
    }

    var elPill = null;
    function ensurePill() {
      var hd = root.querySelector('[data-lfy-collapse]');
      if (!hd) return null;
      if (elPill && elPill.isConnected) return elPill;
      elPill = hd.querySelector('.lfy-sel__hd-pill');
      if (!elPill) {
        elPill = document.createElement('span');
        elPill.className = 'lfy-sel__hd-pill';
        elPill.hidden = true;
        var chev = hd.querySelector('.lfy-sel__hd-chev');
        if (chev) hd.insertBefore(elPill, chev); else hd.appendChild(elPill);
      }
      return elPill;
    }

    function doneTitle() {
      var kind = (hdState.kind === 'globe') ? 'Globe' : 'Driver';
      var nm = hdState.doneName || '';
      /* v4's mock reads "Driver added — 12V 20W IP66", a hand-written spec label. The
         real identity of what is in the cart is the product TITLE, so that is what we
         show; the SHAPE is v4's.

         Trimmed at a WORD boundary. Measured at 768 in the 344px buy column, the full
         "10w 12v DC Weatherproof LED Driver" ran the header to three lines against
         v4's one, and a mid-word cut ("…Weatherpr…") reads as corruption rather than
         as an abbreviation. */
      if (nm.length > 32) {
        var cut = nm.slice(0, 33);
        var sp = cut.lastIndexOf(' ');
        nm = (sp > 14 ? cut.slice(0, sp) : nm.slice(0, 31)).replace(/[\s,·\-–]+$/, '') + '…';
      }
      /* v4's `both` panel: `doneTitle: 'Globe added — switch to Driver'`. On a two-job
         fitting the done moment is the best prompt for the SECOND job, so the title names
         what is still outstanding instead of repeating what was just added. Only while the
         other job is genuinely undone — once both are in the cart it reverts to naming the
         product, because there is nothing left to switch to. */
      if (hdState.both) {
        var other = (hdState.kind === 'globe') ? 'driver' : 'globe';
        if (!doneKinds[other]) {
          return kind + ' added — switch to ' + (other === 'globe' ? 'Globe' : 'Driver');
        }
      }
      return nm ? (kind + ' added — ' + nm) : (kind + ' added');
    }

    function syncHeader() {
      var hd = root.querySelector('[data-lfy-collapse]');
      if (!hd) return;
      var collapsed = hd.getAttribute('aria-expanded') === 'false';
      var elTitle = hd.querySelector('.lfy-sel__hd-title');
      var elSub   = hd.querySelector('.lfy-sel__hd-sub');
      var elIco   = hd.querySelector('.lfy-sel__hd-ico');

      if (hdState.done) {
        root.classList.add('is-done');
        if (elIco && elIco.getAttribute('data-lfy-tick') !== '1') {
          // Keep the original mark so the swap is inspectable, and idempotent.
          elIco.setAttribute('data-lfy-orig-ico', elIco.innerHTML);
          elIco.setAttribute('data-lfy-tick', '1');
          elIco.innerHTML = tickSvg('lfy-sel__svg');
        }
        if (elTitle) {
          if (!elTitle.hasAttribute('data-lfy-orig')) elTitle.setAttribute('data-lfy-orig', elTitle.textContent.trim());
          elTitle.textContent = doneTitle();
        }
        // v4's done sub, verbatim. The locked-constraint sub is preserved in the
        // attribute so nothing is destroyed by a presentation state.
        if (elSub) {
          if (!elSub.hasAttribute('data-lfy-orig')) elSub.setAttribute('data-lfy-orig', elSub.textContent.trim());
          elSub.textContent = 'Tap to change or add another';
        }
      } else if (root.classList.contains('is-done')) {
        /* THE WAY BACK. Until now the done state was one-way — nothing could leave it,
           because nothing ever set hdState.done to false. Emptying the cart from the
           theme's drawer now does, so every substitution above has to be undone from the
           stashes it made rather than re-derived, or the header would keep the tick and
           the "Tap to change" sub over a card offering things to add. */
        root.classList.remove('is-done');
        if (elIco && elIco.getAttribute('data-lfy-tick') === '1') {
          elIco.innerHTML = elIco.getAttribute('data-lfy-orig-ico') || elIco.innerHTML;
          elIco.removeAttribute('data-lfy-tick');
          elIco.removeAttribute('data-lfy-orig-ico');
          syncHdIcon();          // the restored markup shows whatever was visible at add time
        }
        if (elTitle && elTitle.hasAttribute('data-lfy-orig')) {
          elTitle.textContent = elTitle.getAttribute('data-lfy-orig');
          elTitle.removeAttribute('data-lfy-orig');
        }
        if (elSub && elSub.hasAttribute('data-lfy-orig')) {
          elSub.textContent = elSub.getAttribute('data-lfy-orig');
          elSub.removeAttribute('data-lfy-orig');
        }
      }

      var pill = ensurePill();
      if (!pill) return;
      var label = null;
      if (hdState.done) label = 'In cart';
      /* plural() on the jobs form: a dual-need fitting where only ONE kind produced rows
         reports shown = 1, and the hardcoded ' jobs' printed "1 jobs". v4's ' match' form
         is deliberately left uninflected — its state table specifies "2 match". */
      else if (collapsed && hdState.shown > 0) label = hdState.shown + (hdState.both ? ' ' + plural('job', hdState.shown) : ' match');
      if (label === null) { pill.hidden = true; pill.innerHTML = ''; return; }
      pill.innerHTML = (hdState.done ? tickSvg('lfy-sel__done-ico') : '') +
                       '<span>' + esc(label) + '</span>';
      pill.hidden = false;
    }

    /* Replace THIS row's Add with the v4 done treatment. Scoped from the form, never a
       parentElement hop (rule 6c), and null-guarded: a silently-missing button is
       indistinguishable from a working one in any check that does not click it. */
    function markRowDone(form) {
      if (!form || form.getAttribute('data-lfy-done') === '1') return;
      var btn = form.querySelector('.lfy-sel__rcard-add, .lfy-sel__addbtn');
      if (!btn || !btn.parentNode) return;
      var badge = document.createElement('span');
      badge.className = 'lfy-sel__done';
      badge.setAttribute('role', 'status');
      badge.innerHTML = tickSvg('lfy-sel__done-ico') + '<span class="lfy-sel__done-tx">In cart</span>';
      btn.parentNode.replaceChild(badge, btn);
      var sel = form.querySelector('[data-lfy-variant]');
      if (sel) sel.disabled = true;
      form.setAttribute('data-lfy-done', '1');
    }

    /* render()/renderInline() rebuild innerHTML, which would silently resurrect an Add
       button for something already in the cart. Re-apply on SINGLE-variant rows only —
       on a multi-variant row the customer may since have picked a different variant, so
       the hidden id is the only unambiguous evidence. */
    function reapplyDone(scope) {
      if (!scope) return;
      Array.prototype.forEach.call(scope.querySelectorAll('.lfy-sel__add'), function (f) {
        var idf = f.querySelector('input[type="hidden"][name="id"]');
        if (idf && addedIds[String(idf.value)]) markRowDone(f);
      });
    }

    /* ── THE CART IS THE SOURCE OF TRUTH, NOT OUR MEMORY ─────────────────────────────
       Founder, 2026-08-17: "i have added to cart from selector and then remove but still
       showing added in the card".

       `addedIds` was written on add and never read back. Remove the line from the theme's
       drawer and nothing told this component, so the row kept its "In cart" badge, the tab
       kept its ✓ and the header kept "Driver added" — for something no longer being bought.

       The kind is DERIVED from the pools rather than remembered, so reconciliation also
       works on a fresh page load, where no add ever happened in this session but the item
       may still be sitting in the cart. */
    function kindForVariantId(id) {
      id = String(id);
      var hit = function (list) {
        return list.some(function (it) {
          if (String(it.variantId) === id) return true;
          return (it.variants || []).some(function (v) { return String(v.id) === id; });
        });
      };
      if (hit(globes)) return 'globe';
      if (hit(drivers)) return 'driver';
      return null;                            // the fitting itself, or anything else
    }

    /* Rebuilds every "done" flag from an actual cart payload, then re-renders. Nothing is
       un-marked by hand: renderInline() rebuilds the rows from scratch and reapplyDone()
       only re-badges what addedIds still contains, so a removed line simply comes back as a
       normal Add button. */
    function reconcileCart(cart) {
      if (!cart || !cart.items) return;
      var next = {}, kinds = {}, anyKind = null, anyName = '';
      cart.items.forEach(function (li) {
        var k = kindForVariantId(li.variant_id);
        if (!k) return;                       // not one of our accessories
        next[String(li.variant_id)] = true;
        kinds[k] = true;
        anyKind = anyKind || k;
        anyName = anyName || (li.product_title || li.title || '');
      });

      addedIds = next;
      doneKinds = kinds;
      hdState.done = !!anyKind;
      if (anyKind) { hdState.kind = anyKind; hdState.doneName = anyName; }
      else { hdState.doneName = ''; }

      renderInline();
      if (modal && !modal.hasAttribute('hidden')) render();
      syncSeg();
      syncHeader();
    }

    /* The theme announces every cart change on `document` as `cart-update`, carrying the
       cart itself (theme.js:3696 and :3871) — the same event its own drawer, quantity
       steppers and remove links fire. Listening to it means a removal anywhere on the page
       reaches this component without polling. */
    document.addEventListener('cart-update', function (e) { reconcileCart(e.detail); });

    /* And once at start, because the cart survives a reload while this module's memory does
       not: an accessory added, then the page refreshed, should still read "In cart". */
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c) reconcileCart(c); })
      .catch(function () { /* a cart we cannot read leaves the card in its default state */ });

    function markAdded(form, id) {
      addedIds[String(id)] = true;
      var row = form.closest('.lfy-sel__rcard') || form.closest('.lfy-sel__card');
      var nm = row ? row.querySelector('.lfy-sel__rcard-nm, .lfy-sel__card-title') : null;
      hdState.done = true;
      hdState.doneName = nm ? nm.textContent.trim() : '';
      /* Prefer the ROW's own kind. A dual-need card now shows globes and drivers at once,
         so hdState.inlineKind is empty there by design and reading it (or `mode`, which
         starts at 'globe') would title a driver add as a globe. The popup is still
         single-kind at a time, so its `mode` remains correct. */
      var rowKind = row ? row.getAttribute('data-lfy-kind') : null;
      hdState.kind = (modal && modal.contains(form)) ? mode
                   : (rowKind || hdState.inlineKind || mode);
      doneKinds[hdState.kind] = true;
      markRowDone(form);
      syncSeg();
      syncHeader();
    }

    /* ── THE SEGMENTED CONTROL ───────────────────────────────────────────────────────
       Reflects `mode` onto the two tabs and ticks whichever job is finished. Called after
       every render and after every add, never assumed: the tab state and `mode` drifting
       apart would show a Globe list under a highlighted Driver tab. */
    /* The header glyph belongs to the ACTIVE KIND, not to the fitting. v4 draws the `both`
       panel with `selIco: globeIcon`; Inline C's panel 3b redraws the same header with
       `drvSvg` once the Driver tab is on. A single-need fitting emits one glyph and this is
       a no-op for it.

       Skipped entirely while the done state owns the icon — syncHeader() has replaced the
       span's contents with a tick by then, and writing here would fight it. The revert path
       calls this again once the tick is removed. */
    function syncHdIcon() {
      var ico = root.querySelector('.lfy-sel__hd-ico');
      if (!ico || ico.getAttribute('data-lfy-tick') === '1') return;
      var glyphs = ico.querySelectorAll('[data-lfy-hdico]');
      if (glyphs.length < 2) return;
      Array.prototype.forEach.call(glyphs, function (g) {
        if (g.getAttribute('data-lfy-hdico') === mode) g.removeAttribute('hidden');
        else g.setAttribute('hidden', '');
      });
    }

    function syncSeg() {
      var seg = root.querySelector('[data-lfy-seg]');
      if (!seg) return;                       // single-need fitting renders no control
      Array.prototype.forEach.call(seg.querySelectorAll('[data-lfy-segbtn]'), function (b) {
        var k = b.getAttribute('data-lfy-segbtn');
        var on = (k === mode);
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        var tick = b.querySelector('[data-lfy-segtick]');
        if (tick) { if (doneKinds[k]) tick.removeAttribute('hidden'); else tick.setAttribute('hidden', ''); }
      });
      syncHdIcon();
    }

    /* Switching tab is the same state change the popup's tabs make, so it resets the facet
       choices and the show-all flag exactly as they do — leaving a globe colour-temperature
       chip selected while showing drivers would filter the driver list by a facet the
       customer cannot see. */
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-lfy-segbtn]');
      if (!btn || !root.contains(btn)) return;
      var next = btn.getAttribute('data-lfy-segbtn');
      if (!next || next === mode) return;
      mode = next;
      choices = {}; showAll = false;
      syncSeg();
      renderInline();
      /* Keep the popup honest if it is already open behind the card — one `mode`, two
         surfaces, and rule 5j says the two must not drift. */
      if (modal && !modal.hasAttribute('hidden')) {
        Array.prototype.forEach.call(modal.querySelectorAll('[data-lfy-tab]'), function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-lfy-tab') === mode);
        });
        renderFilters(); render();
      }
    });

    /* CONTEXT PLACEMENT — founder: the reason + locked row should sit at the BOTTOM,
       after the warranties panel, on desktop. On this template the four collapsible
       panels render inside the buy column, so "after warranties" means after the last
       panel in that same column. We MOVE the existing node rather than re-render it,
       so nothing in the buy column is rebuilt (PDP JS contract) and no listener is
       orphaned. Below 1025px it stays where it is: on a phone the panels are far down
       the page and the context would be lost. */
    /* Collapse, as the mockup's chevron implies. Purely presentational; the card
       starts open because the whole point of phase 1 is that the matches are visible
       without a click. */
    var elHd = root.querySelector('[data-lfy-collapse]');
    var elBd = root.querySelector('[data-lfy-body]');
    if (elHd && elBd) {
      // Server renders it CLOSED, so sync the class before any interaction —
      // otherwise the chevron and the body disagree on first paint.
      if (elHd.getAttribute('aria-expanded') === 'false') root.classList.add('is-collapsed');

      /* OPEN STATE BY VIEWPORT, from the v4 panel definitions:
           390  selState 'closed'  -> collapsed, tap to open
           768  selState 'closed'  -> collapsed, one line + "2 match" pill
          1280  (no selState)      -> "stays 2 column · selector stays inline" — OPEN
          1600  (no selState)      -> "finder EXPANDED in the rail"
         The build collapsed everywhere, so at >=1500 the rail rendered a 98px stub
         instead of the finder — the founder saw an almost-empty third column.

         ⛔ THRESHOLD IS 1280, NOT 1500 (founder, 2026-08-18). The first pass read only
         the 1600 panel and opened at the rail breakpoint, which left 1280-1499 collapsed
         even though v4 gives its 1280 panel no selState either — i.e. open. Only the two
         panels that SAY 'closed' are closed. This is the mock's own number and is not the
         rail's 1500; the two are unrelated and 1500 stays where it is elsewhere.

         Only force-open once, and never fight a customer who has closed it. */
      var userToggled = false;
      elHd.addEventListener('click', function () { userToggled = true; }, true);
      function syncOpenForViewport() {
        if (userToggled) return;
        var wantOpen = window.innerWidth >= 1280;   // v4's desktop panel; 390/768 stay closed
        var isOpen = elHd.getAttribute('aria-expanded') !== 'false';
        if (wantOpen === isOpen) return;
        elHd.setAttribute('aria-expanded', wantOpen ? 'true' : 'false');
        elBd.hidden = !wantOpen;
        root.classList.toggle('is-collapsed', !wantOpen);
        syncHeader();                   // the pill exists only while collapsed (v4)
      }
      syncOpenForViewport();
      window.addEventListener('resize', function () { setTimeout(syncOpenForViewport, 160); });
      elHd.addEventListener('click', function () {
        var open = elHd.getAttribute('aria-expanded') !== 'false';
        elHd.setAttribute('aria-expanded', open ? 'false' : 'true');
        elBd.hidden = open;
        root.classList.toggle('is-collapsed', open);
        syncHeader();
      });
    }

    renderInline();
    syncHeader();     // covers renderInline()'s early returns (no rows -> no pill)

    /* The OTHER way tiers 1-2 come up empty, and the one a pool-size check misses.
       renderInline() can rank down to zero rows even from a non-empty pool — every
       candidate lands in `unstated`, so `ranked` is empty and the card renders as a
       bare header with no rows and no "see all". That is a tier that yielded no
       result, so the chain moves on exactly as it does for an empty pool. Gating
       only on `globes.length && drivers.length` would leave a header-only card in
       the rail and never reach tier 3. */
    /* ⚠ `loadSuppressedRows` guards this. When the LOAD filter is why no rows rendered,
       tiers 1-2 did NOT come up empty — they came up full of drivers that are too small
       for this particular run, and the card is showing the customer exactly that, with
       the split-the-run advice. Handing the rail to the best-sellers fallback there
       would hide the answer and replace it with unmatched stock. */
    /* ALWAYS, not only when the finder came up empty (founder 2026-08-18). runFallback()
       still refuses below 1500, where there is no rail to put it in, and still renders
       nothing if neither tier yields a full row set. The companion/replacement decision is
       made inside it, where the row counts are known. */
    fbEligible = true; runFallback();

    /* After the first render, never before it. setTimeout rather than requestIdleCallback:
       Safari has no rIC, and this must not be the one browser where the pool stays at 50.
       Both pools are scheduled together and each returns immediately when its own fitting
       does not need that accessory, so a globe-only fitting still issues no driver request. */
    window.setTimeout(topUpGlobePool, 400);
    window.setTimeout(topUpDriverPool, 500);

    // React to variant switching using the theme's own change event.
    if (typeof wireVariantReactivity === 'function') wireVariantReactivity(root, fitting);

    /* ── THE 1500 RAIL (v4 spec, founder ruling D1-k: 1500 not 1600) ──────────
       At >=1500px the selector moves into a third column. `.productView-top` is
       already flex/nowrap with two children, so the rail is one appended child.

       MOVED, never re-rendered. Re-rendering the buy column is what the PDP JS
       contract forbids, and a fresh render would orphan every listener bound to
       this subtree — which is precisely the defect class already shipped twice
       (rule 6c via a wrapper, rule 6d via a form= attribute). appendChild keeps
       the same nodes and the same handlers.

       A placeholder marks the original position so the move is reversible: below
       1500 the card returns exactly where Liquid put it, not to the end of the
       buy column. */
    var railHome = null, railEl = null, placeholder = null;

    function ensureRail() {
      var top = document.querySelector('.productView-top');
      if (!top) return null;
      if (!railEl || !railEl.isConnected) {
        railEl = top.querySelector(':scope > .lfy-rail');
        if (!railEl) {
          railEl = document.createElement('div');
          railEl.className = 'lfy-rail';
          top.appendChild(railEl);
        }
      }
      return railEl;
    }

    function placeSelector() {
      var wide = window.innerWidth >= 1500;
      if (wide) {
        var rail = ensureRail();
        if (!rail || root.parentElement === rail) return;
        if (!placeholder) {
          placeholder = document.createComment('lfy-selector-home');
          root.parentNode.insertBefore(placeholder, root);
          railHome = true;
        }
        rail.appendChild(root);              // MOVE — listeners survive
      } else if (railHome && placeholder && root.parentElement !== placeholder.parentNode) {
        placeholder.parentNode.insertBefore(root, placeholder);
      }
    }

    placeSelector();
    var railTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(railTimer);
      railTimer = setTimeout(function () {
        placeSelector();
        // A customer who widens past 1500 gets the rail; if tiers 1-2 were empty
        // the fallback has to be fetched at that point, not at load, because at
        // load there was no rail to put it in.
        if (fbEligible) runFallback();
      }, 150);
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-lfy-selector]'), init);
  }

  /* ── VARIANT REACTIVITY ───────────────────────────────────────────────────
     The row reacts to the selected variant the same way price and stock do.

     HOW IT HOOKS IN: the theme keeps a hidden `input[name="id"]` inside the product
     form in sync and fires a native `change` on it (variants.js:159/575). Verified in
     the browser: switching PART NUMBER moved it 39949200719956 -> 39954134827092 and
     fired exactly one change. So we listen to the theme's OWN event — no second
     MutationObserver, no polling, nothing bound to .productView-options. The PDP JS
     contract holds: variants.js still binds once and we never re-render the buy column.

     WHY WE PARSE THE TITLE, NOT THE OPTION NAME: across the 107 mixed-voltage listings
     the voltage sits under "Color" (54), "Style" (39), "Voltage" (37), "PART NUMBER"
     (18), "Model" (4) and "Variant" (3). The option NAME is meaningless — PENDING P8.
     Self-tested against all 357 real variant titles: 99.2% parsed, and the 3 that do
     not parse fall back to showing BOTH options rather than guessing or hiding.
     A hidden row on a product that needs a driver is a SILENT failure. */
  var SOCKET_RE = /\b(E27|E14|E12|E26|B22|B15|GU10|GU5\.3|MR16|MR11|G9|G4|G5|GX53|T5|T8|R7S)\b/i;

  function variantVoltage(text) {
    var t = String(text || '');
    var low = /(?:^|[^\d])(?:12|24)\s*v\b/i.test(t) || /-\s*(?:12|24)v\b/i.test(t);
    var mains = /(?:^|[^\d])(?:240|220)\s*v\b/i.test(t) || /-\s*(?:240|220)v\b/i.test(t);
    if (low && mains) return 'BOTH';
    if (low) return 'LOW';
    if (mains) return 'MAINS';
    return 'UNKNOWN';                    // never guess
  }
  function variantSocket(text) {
    var t = String(text || '');
    var m = t.match(SOCKET_RE);
    if (!m && /built[- ]?in led|no globe/i.test(t)) return null;
    return m ? m[1].toUpperCase() : null;
  }

  /* Resolve the selected variant's title+sku.
     Primary source is the variantMap keyed by the hidden input's id — verified: the
     option selects live OUTSIDE the product form on this theme and their values are
     part numbers ("HV1005T") with no voltage in them, so reading the controls
     returned an empty string and everything parsed UNKNOWN.
     The control values are kept only as a secondary signal (some products DO put
     "12v" in the option value). Returns '' when nothing is resolvable — which the
     caller treats as UNKNOWN and shows both options. */
  function currentVariantText(fitting, idInput) {
    var bits = [];
    var map = fitting.variantMap || {};
    if (idInput && idInput.value && map[idInput.value]) bits.push(map[idInput.value]);
    Array.prototype.forEach.call(document.querySelectorAll('select[name^="options"]'), function (s) {
      if (s.value) bits.push(s.value);
    });
    Array.prototype.forEach.call(document.querySelectorAll('input[type="radio"][name^="options"]:checked'), function (r) {
      if (r.value) bits.push(r.value);
    });
    return bits.join(' ');
  }

  function wireVariantReactivity(root, fitting) {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;

    var btnGlobe = root.querySelector('[data-open-mode="globe"]');
    var btnDriver = root.querySelector('[data-open-mode="driver"]');
    if (!btnGlobe && !btnDriver) return;

    function label(btn, text) {
      if (!btn) return;
      var el = btn.querySelector('.lfy-sel__trigger-text');
      if (el) el.textContent = text;
    }

    function apply() {
      var txt = currentVariantText(fitting, idInput);
      var v = variantVoltage(txt);
      var sock = variantSocket(txt);

      // Product-level capability is the ceiling; the variant only narrows it.
      var canGlobe = fitting.needsGlobe;
      var canDriver = fitting.needsDriver;

      var wantDriver, wantGlobe;
      if (v === 'MAINS')      { wantDriver = false;     wantGlobe = canGlobe; }
      else if (v === 'LOW')   { wantDriver = canDriver; wantGlobe = canGlobe; }
      // UNKNOWN or BOTH: show everything the PRODUCT supports and make no claim.
      // Hiding on an unparsed title would be a silent failure.
      else                    { wantDriver = canDriver; wantGlobe = canGlobe; }

      // A variant naming its own socket is definitive for the globe side.
      if (sock) wantGlobe = true;
      // 'Built in LED' or 'No Globe' on the variant means no replaceable globe.
      if (/built[- ]?in led|no globe/i.test(txt) && !sock) wantGlobe = false;

      // A button can only be shown if it was RENDERED. The server only emits the
      // globe button when the PRODUCT has a real socket; asking for globe mode on a
      // product with no globe button leaves a visible row with nothing in it.
      // (Caught in verification: a 240V built-in-LED variant reported mode="globe"
      // with zero visible buttons — a silently empty row.)
      if (!btnGlobe) wantGlobe = false;
      if (!btnDriver) wantDriver = false;

      // Needs neither -> hide the row entirely.
      if (!wantDriver && !wantGlobe) { root.setAttribute('hidden', ''); return; }
      root.removeAttribute('hidden');

      var both = wantDriver && wantGlobe;
      if (btnGlobe)  btnGlobe.style.display  = wantGlobe  ? '' : 'none';
      if (btnDriver) btnDriver.style.display = wantDriver ? '' : 'none';

      // Short labels when both are shown side by side; the full sentence when alone.
      label(btnGlobe,  both ? 'Find the right globe'  : 'Find the right globe for this fitting');
      label(btnDriver, both ? 'Find the right driver' : 'Find the right driver for this fitting');

      var rowEl = root.querySelector('.lfy-sel__row');
      if (rowEl) rowEl.classList.toggle('lfy-sel__row--two', both);

      root.setAttribute('data-mode', both ? 'both' : (wantGlobe ? 'globe' : 'driver'));
      root.setAttribute('data-variant-voltage', v);
    }

    idInput.addEventListener('change', apply);   // the theme's OWN event
    apply();
  }
  window.__lfyWireVariant = wireVariantReactivity;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
